/**
 * Application-wide constants
 */

import config from "@/config";
import { resolveApiUrl } from "@/config/localDev";
// Type-only: keeps the constants ↔ useRbac edge erased at build time, so
// `useRbac`'s own `ADMIN_ROLES` import can't close a runtime import cycle.
import type { InvitedUsersParams } from "@/hooks/useRbac";
import { toNumber } from "@/utils/dashboardFormat";

/**
 * Formats a cost-in-USD value with a fixed 4 decimal places, e.g. "$0.2753".
 * Coerces through toNumber first — a backend numeric column arriving as a
 * string (confirmed live cause of a crash: Postgres NUMERIC summed via raw
 * SQL returns a Decimal that serializes to a JSON string, not a number) must
 * not reach `.toFixed()` directly, which throws on a string. The `| string`
 * in the signature is the point, not a loophole — it documents at every call
 * site that this value's wire type has already broken its stated contract
 * once in production.
 */
const formatCostUsd = (value: number | string): string => `$${(toNumber(value) ?? 0).toFixed(4)}`;

/** Cookie names the app reads and writes: the auth token, the global cookie, and the per-user cache keys. */
export const COOKIES = {
  AUTH_TOKEN: "saras_auth_token",
  saras_global: `${import.meta.env.VITE_ENVIRONMENT_TYPE}_global`,
  cacheKeysEnum: {
    saras_user_details: "saras_user_details",
    saras_user_id: "saras_user_id",
    saras_auth_key: "saras_auth_key",
    saras_custom_auth_key: "saras_custom_auth_key",
    saras_global_portal: "saras_global_portal",
    isEmailVerified: "isEmailVerified",
    warehouse: "saras_warehouse",
  },
};

// Domain related constants
export const DOMAINS = {
  LOCAL: "localhost",
  MAIN: ".sarasanalytics.com",
};

// The URL path segment used for shared-chat routes (`/chat/share/:token`).
// Single source of truth: ROUTES.CHAT_SHARE, the Threads route definition,
// the Threads URL-sync guard, and isChatSectionPath all derive from this.
export const CHAT_SHARE_SEGMENT = "share";

// The Settings sub-path for Manage Group (`/settings/groups`) — a noun, not the
// camelCase verb-phrase "manageGroup" the route used to spell out separately
// in three places. This segment is the single source of truth: the Settings
// section entry, the `<Route path>`, and the breadcrumb all derive from it.
// `ROUTES.SETTINGS_MANAGE_GROUP` (built from this same segment) has no reader
// yet — reserved for an external deep link.
export const SETTINGS_MANAGE_GROUP_SEGMENT = "groups";

// The Settings sub-path for personal Instructions (`/settings/instructions`).
// Single source of truth: ROUTES.SETTINGS_INSTRUCTIONS, the Settings section
// entry + route, and the breadcrumb all derive from this.
export const SETTINGS_INSTRUCTIONS_SEGMENT = "instructions";

// The Settings sub-path for Memories (`/settings/memories`). Single source of
// truth for ROUTES.SETTINGS_MEMORIES and the Settings section entry + route.
export const SETTINGS_MEMORIES_SEGMENT = "memories";

/** An MCP client with a setup guide under Settings > Integrations. */
export type McpGuideClient = "claude" | "codex" | "grok" | "gemini";

/**
 * The one registry of MCP setup guides: each client's route segment
 * (`/settings/integrations/<segment>`) and page label (breadcrumb crumb).
 * `ROUTES.SETTINGS_MCP_GUIDES`, the Settings routes, the breadcrumbs, the
 * Integrations rows and the guide pages all read it. Kept here, not in
 * `mcpGuides.tsx`, so the route table never pulls in the guide copy and mockups.
 */
export const MCP_GUIDE_META: Record<McpGuideClient, { segment: string; label: string }> = {
  claude: { segment: "claude-mcp-guide", label: "Claude MCP Setup" },
  codex: { segment: "codex-mcp-guide", label: "Codex MCP Setup" },
  grok: { segment: "grok-mcp-guide", label: "Grok MCP Setup" },
  gemini: { segment: "gemini-mcp-guide", label: "Gemini MCP Setup" },
};

/** The MCP guide clients in display order (the Integrations row order). */
export const MCP_GUIDE_CLIENTS = Object.keys(MCP_GUIDE_META) as McpGuideClient[];

/** The Saras iQ MCP server endpoint. Every client (Claude, Codex, Grok, Gemini) connects to it. */
export const MCP_SERVER_URL = `${import.meta.env.VITE_IQ_API_URL}/mcp/v2`;

// Pre-rename path of the Claude guide; redirects so old links keep working.
export const LEGACY_MCP_SETUP_SEGMENT = "mcp-setup";

// Root of every Settings > Instructions write mutation key. Lives here (not in
// the feature hook) so AuthContext can purge a previous user's pending/unseen
// writes on sign-out without importing feature code into the app shell.
export const USER_INSTRUCTIONS_WRITE_KEY_ROOT = "user-instructions-write";

// Mirrors the backend's MAX_INSTRUCTION_MARKDOWN_CHARS (counted in code points
// over the markdown string). The counter warns from NEAR_RATIO of the limit.
export const USER_INSTRUCTIONS_LIMITS = {
  MAX_CHARS: 1500,
  NEAR_RATIO: 0.9,
} as const;

// Routes
export const ROUTES = {
  HOME: "/",
  AUTH: "/auth",
  CHAT: "/chat",
  LOGOUT: "/logout",
  DASHBOARD: "/dashboard",
  // Dashboards workspace (separate top-level shell with its own nav). The
  // singular `/dashboard/*` paths now redirect here for back-compat.
  DASHBOARDS_BASE: "/dashboards",
  // Chats library page — the full list of the user's conversations (search +
  // relative times), reached from the "Chats" rail destination and each Recent
  // chats "View all". Renders as a content-only inner route of the chat shell.
  CHATS_LIBRARY: "/chats",
  // Shared chat read-only view — rendered inside the authenticated chat shell
  // so User B must be logged in (same-company requirement).
  CHAT_SHARE: (token: string) => `/chat/${CHAT_SHARE_SEGMENT}/${token}`,
  DASHBOARDS_LIVE: "/dashboards/live",
  // End-user "Dashboards home" — a grid of the use cases the user can access.
  // Rendered as a Level-1 route in the chat shell (`Threads.tsx`), OUTSIDE the
  // creator `iq-dashboards` flag + `creatorOnly` gates, so any authenticated user
  // can reach it. It's where the "Dashboards" breadcrumb crumb takes an end user.
  DASHBOARDS_USE_CASES: "/dashboards/use-cases",
  DASHBOARDS_DRAFTS: "/dashboards/drafts",
  DASHBOARDS_NEW: "/dashboards/new",
  DASHBOARDS_DRAFT: (chatId: number | string) => `/dashboards/draft/${chatId}`,
  DASHBOARDS_VIEW: (id: string) => `/dashboards/live/${id}`,
  // Use-Case dashboard view — the END-USER experience, rendered INSIDE the chat
  // shell (Threads → NavMain), NOT the creator `/dashboards` workspace. A use case
  // (nav item) opens a page where its dashboards are top tabs. Singular `/dashboard`
  // so it's owned by the chat shell (`/dashboards/*`, plural, is the workspace); two
  // dynamic segments rank above the legacy `dashboard/*` redirect (which still
  // catches single-segment links).
  DASHBOARDS_USE_CASE_VIEW: (useCaseId: number | string, dashboardId: string) =>
    `/dashboard/${useCaseId}/${dashboardId}`,
  // Creator preview of that end-user view, in the same chat shell. Catalog-sourced
  // (works for any use case the creator manages, even before a company is entitled).
  // The literal `preview` segment outranks the `:useCaseId/:dashboardId` route.
  DASHBOARDS_USE_CASE_PREVIEW: (useCaseId: number | string, dashboardId: string) =>
    `/dashboard/preview/${useCaseId}/${dashboardId}`,
  // Creator-only management surfaces, split into three peer pages (each its own
  // nav item + route). The base path redirects to the Use Cases tab.
  DASHBOARDS_MANAGE: "/dashboards/manage",
  DASHBOARDS_MANAGE_USE_CASES: "/dashboards/manage/use-cases",
  DASHBOARDS_MANAGE_ASSIGNMENTS: "/dashboards/manage/assignments",
  DASHBOARDS_MANAGE_ENTITLEMENTS: "/dashboards/manage/entitlements",
  // Admin workspace (separate top-level shell with its own nav).
  ADMIN_BASE: "/admin",
  ADMIN_GODVIEW: "/admin/godview",
  // Adoption Metrics is tabbed; the tab lives in the URL so each tab is a
  // deep-linkable left-nav sub-item. The base path redirects to the `users` tab.
  ADMIN_ADOPTION_METRICS: "/admin/adoption-metrics-dashboard",
  ADMIN_ADOPTION_METRICS_TAB: (tab: "users" | "subscriptions" | "errors") =>
    `/admin/adoption-metrics-dashboard/${tab}`,
  ADMIN_FEEDBACK: "/admin/feedback",
  ADMIN_SUBSCRIPTIONS: "/admin/subscriptions",
  SETTINGS_BASE: "/settings",
  // Context Console — the self-serve context upload/evaluate/publish flow for IQ
  // Admins. Lives under Settings (profile menu → Settings), not the /admin shell —
  // it's a company-scoped admin action, not a Saras-staff one.
  SETTINGS_CONTEXT_CONSOLE: "/settings/contextConsole",
  // Memories — the signed-in user's saved memories (Settings section id "memories").
  SETTINGS_MEMORIES: `/settings/${SETTINGS_MEMORIES_SEGMENT}`,
  // Manage Group — data access groups (brands/tags/members).
  SETTINGS_MANAGE_GROUP: `/settings/${SETTINGS_MANAGE_GROUP_SEGMENT}`,
  // Instructions — personal guidance iQ follows for the signed-in user.
  SETTINGS_INSTRUCTIONS: `/settings/${SETTINGS_INSTRUCTIONS_SEGMENT}`,
  SETTINGS_INTEGRATIONS: "/settings/integrations",
  // Reports — top-level section; the route lives INSIDE the app shell.
  REPORTS: "/reports",
  // My Requests — top-level section; the route lives INSIDE the app shell.
  REQUESTS: "/requests",
  SLACK_CONNECT_STATUS: "/slack-connect/status",
  // iQ setup wizard (D12 gate-exempt).
  SETUP: "/setup",
  SETUP_WIZARD: "/setup/wizard",
  /** Each MCP setup guide's path, keyed by client (segments from `MCP_GUIDE_META`). */
  SETTINGS_MCP_GUIDES: Object.fromEntries(
    MCP_GUIDE_CLIENTS.map((client) => [client, `/settings/integrations/${MCP_GUIDE_META[client].segment}`]),
  ) as Record<McpGuideClient, string>
};

/**
 * Static asset served from `public/context-console-templates.zip` — the
 * starter template pack Context Console links to for a fresh setup.
 * `BASE_URL`, not a bare "/", so this still resolves if the app is ever
 * served from a sub-path.
 */
export const CONTEXT_CONSOLE_TEMPLATES_URL = `${import.meta.env.BASE_URL}context-console-templates.zip`;

// API endpoints
const baseAPI = `${import.meta.env.VITE_IQ_API_URL}`;
// Single source of truth for the schedule resource path, shared by
// API.REPORTS.UPDATE_SCHEDULE (PATCH) and SCHEDULE_DETAIL (GET) below — both
// verbs hit the same URL, and deriving from one function keeps them from ever
// silently desyncing if this path changes.
const scheduleResourcePath = (scheduleId: number) => `${baseAPI}/reports/schedule/${scheduleId}`;
// In local-dev mode the user service is routed at the local backend (see
// resolveApiUrl); otherwise it points at the configured dev/prod user service.
const userServiceAPI = resolveApiUrl(import.meta.env.VITE_USER_SERVICE_API_URL);
// The user service WITHOUT the local-dev reroute. Some user-service routes (e.g.
// the company catalog `/company/companies`) have no local-backend equivalent, so
// they must hit the real user service directly even under `pnpm local`. Use this
// base for those; `.env.localdev` sets VITE_USER_SERVICE_API_URL to the dev user
// service so it resolves in localdev too.
const rawUserServiceAPI = import.meta.env.VITE_USER_SERVICE_API_URL;
// Local escape hatch: point ONLY the download API elsewhere (e.g. deviqapi) when
// the local backend's download/user service is unavailable. Defaults to baseAPI,
// so this is a no-op unless VITE_DOWNLOAD_API_URL is set (in .env.development).
const downloadAPI = import.meta.env.VITE_DOWNLOAD_API_URL || baseAPI;

/**
 * The user service's IQ user directory (`GET /user?productType=IQ` →
 * `{ data: User[], errors: [] }`). Hoisted because two API entries name the same
 * endpoint for the adoption dashboard (`ADOPTION_METRICS.USERS`).
 *
 * NOT to be confused with `API.METRICS.USERS`, which is iq-backend's
 * `/metrics/users` aggregate — a different service and a different payload.
 */
const iqUserDirectoryUrl = `${userServiceAPI}/user?productType=IQ`;

/**
 * Default `max_rows` sent with `POST /dashboards/queries/execute`. `0` means
 * "no client-imposed cap — use the backend default". Bump this if widgets need a
 * hard ceiling on returned rows.
 */
export const DASHBOARD_QUERY_MAX_ROWS = 0;

/**
 * Request headers the app sets itself (beyond `Authorization`/`Content-Type`).
 *
 * `DO_AS_USER` is the SarasAdmin "View as user" impersonation header: when present
 * alongside the admin's own Bearer token, iq-backend runs the WHOLE request as the
 * target user (their company, entitlements and caches). It is attached ONLY to
 * dashboard/use-case data requests (see `isDoAsScopedUrl` in
 * `services/viewAsUser.ts`) — never app-wide, never on admin/auth/user-service
 * endpoints.
 */
export const HEADERS = {
  DO_AS_USER: "X-Do-As-User-Id",
} as const;

/**
 * A widget whose data took longer than this (batch start → its SSE result) emits a
 * `dashboard_widget_slow` detail event, once per widget per load. Detail volume
 * therefore scales with PROBLEMS, not with traffic — raising this threshold makes
 * the feed quieter, lowering it makes it noisier.
 */
export const SLOW_WIDGET_MS = 5000;

/**
 * localStorage key prefix of a reader's own matrix view (pins + period sort),
 * stored per reader and widget as `${prefix}:${viewerId}:${dashboardId}:${widgetId}`.
 * Bump the version when the stored value's shape changes; old entries are then
 * simply never read.
 */
export const WIDGET_VIEW_PREFS_KEY_PREFIX = "iq.dash.view.v1";

/** Every backend endpoint the app calls, built from the `VITE_*` base URLs. */
export const API = {
  BASE_URL: `${import.meta.env.VITE_IQ_API_URL}`,
  AUTH: `${baseAPI}/auth`,
  USER: `${baseAPI}/user`,
  CHATS: `${baseAPI}/chats`,
  MEMORIES: `${baseAPI}/api/memories`,
  MEMORY: (slug: string) => `${baseAPI}/api/memories/${encodeURIComponent(slug)}`,
  MEMORIES_SETTINGS: `${baseAPI}/api/memories/settings`,
  MEMORY_SETTINGS_USERS: `${baseAPI}/api/memories/settings/users`,
  MEMORY_SETTINGS_USER: (userId: number) => `${baseAPI}/api/memories/settings/users/${userId}`,
  MEMORY_SUGGESTION_ACCEPT: (suggestionId: string) =>
    `${baseAPI}/api/memories/suggestions/${encodeURIComponent(suggestionId)}/accept`,
  MEMORY_SUGGESTION_DISMISS: (suggestionId: string) =>
    `${baseAPI}/api/memories/suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
  // Caller-scoped settings singleton: GET/POST the document, PATCH its status.
  USER_INSTRUCTIONS: `${baseAPI}/api/user-instructions`,
  USER_INSTRUCTIONS_STATUS: `${baseAPI}/api/user-instructions/status`,
  DASHBOARDS: `${baseAPI}/dashboards`,
  // Unsaved dashboard draft conversations: `is_dashboard_chat` chats not yet
  // promoted to a saved dashboard version. Listed in the left-nav "Drafts"
  // section so creators can resume work in progress (load a draft's transcript
  // via the chat-message endpoints with its chat_id).
  DASHBOARD_DRAFTS: `${baseAPI}/dashboards/drafts`,
  // Generates a dashboard spec from a natural-language prompt (LLM decomposition).
  DASHBOARD_GENERATE: `${baseAPI}/dashboards/generate`,
  DASHBOARD_BY_ID: (id: string) => `${baseAPI}/dashboards/${id}`,
  // Admin-only: the full creation conversation (user prompts + assistant replies)
  // behind a published dashboard's on-screen version. Gated server-side on
  // `iq:admin:dashboard:write`; 403 under "View as user", 404 for an unknown id.
  DASHBOARD_ADMIN_CONVERSATION: (id: string) => `${baseAPI}/dashboards/admin/${id}/conversation`,
  // GET: the version history, newest first (admin-only, `iq:admin:dashboard:write`).
  // POST: create a version (used by `createDashboardVersion`).
  DASHBOARD_VERSIONS: (id: string) => `${baseAPI}/dashboards/${id}/versions`,
  // Widget instruction edit (admin-only, exact `iq:admin:dashboard:write`). The query
  // rows behind a version's widgets (`?version_id=`), with each creator's own text.
  DASHBOARD_QUERY_ROWS: (id: string) => `${baseAPI}/dashboards/${id}/queries`,
  // Create a draft version that changes widget instructions. Not the unsaved
  // create-flow chats (`DASHBOARD_DRAFTS`): this is a version that is not current.
  DASHBOARD_VERSION_DRAFTS: (id: string) => `${baseAPI}/dashboards/${id}/drafts`,
  // Generate and run a draft's changed widgets for one company (10 to 60 seconds).
  DASHBOARD_VERSION_DRAFT_TEST: (versionId: string) =>
    `${baseAPI}/dashboards/drafts/${versionId}/test`,
  // Make a tested draft current. 409 when the current version moved; 400 when untested.
  DASHBOARD_VERSION_DRAFT_PUBLISH: (versionId: string) =>
    `${baseAPI}/dashboards/drafts/${versionId}/publish`,
  // Discard a draft: it leaves History. 409 when the version is already published.
  DASHBOARD_VERSION_DRAFT_DISCARD: (versionId: string) =>
    `${baseAPI}/dashboards/drafts/${versionId}/discard`,
  // Bring an old published version back as a new current version.
  DASHBOARD_VERSION_RESTORE: (id: string, versionId: string) =>
    `${baseAPI}/dashboards/${id}/versions/${versionId}/restore`,
  // Batch-executes a set of persisted DashboardQuery ids against the active filter
  // values -> per-query rows. Generates SQL first if a query has none yet. A single
  // bad query is reported in its `error` field and does not abort the rest.
  DASHBOARD_QUERIES_EXECUTE: `${baseAPI}/dashboards/queries/execute`,
  // Shared-dashboard variant of the execute above, for viewers who are not the
  // dashboard's owner (use-case consume/preview views). Authorized by share grant
  // or entitlement (`assert_dashboard_access`) and served from COMPANY-scoped
  // SQL/result caches, so cross-company shares run SQL generated for the viewer's
  // own warehouse. Same body and SSE contract as the owner execute.
  DASHBOARD_SHARED_QUERIES_EXECUTE: (dashboardId: string) =>
    `${baseAPI}/dashboards/${dashboardId}/queries/execute`,
  // Cache purge: deletes the dashboard query result cache rows written by a user
  // (optionally scoped to a company) plus the backend's hot cache, so the next
  // execute recomputes from the warehouse. Body `{ user_id, company_id? }`.
  // NOTE: still guarded SarasAdmin/IQDashboardAdmin-only on the backend; the
  // widening to every role is pending deploy (see `clearDashboardQueryResultCache`).
  DASHBOARD_QUERY_RESULT_CACHE_CLEAR: `${baseAPI}/dashboards/query-result-cache/clear`,
  // Admin "hard refresh" (every dashboard view): clears the GENERATED SQL for the given queries —
  // the per-query SQL, the company-shared SQL row, and the company result cache —
  // so the next execute regenerates SQL before it fetches data. Role-gated to
  // SarasAdmin/IQDashboardAdmin, any dashboard. Body `{ query_ids }`; answers
  // with the affected query rows.
  DASHBOARD_QUERIES_CLEAR: `${baseAPI}/dashboards/queries/clear`,
  // Cross-company variant of the clear above: drops ANOTHER company's cached
  // GENERATED SQL for the given queries. A dashboard shared to other companies is
  // cached per company, so when its SQL goes stale an admin has to invalidate the
  // TARGET company's copy — we can only CLEAR it, their next visit regenerates.
  // Same permission as the own-company clear (`iq:admin:dashboard_sql:write`).
  // Body `{ query_ids }`; 400 on an empty list, 404 when a query id is outside
  // that company's cache scope.
  DASHBOARD_COMPANY_QUERIES_CLEAR: (companyId: number) =>
    `${baseAPI}/dashboards/admin/companies/${companyId}/queries/clear`,
  // Reader-filed "this data looks wrong" report against one widget's query. The
  // body carries only the description — the backend snapshots the SQL and the
  // instruction behind the result the reporter was looking at.
  DASHBOARD_QUERY_REPORT_ISSUE: (dashboardId: string, queryId: string) =>
    `${baseAPI}/dashboards/${dashboardId}/queries/${queryId}/report-issue`,
  // Conversational dashboard creation rides the chat message stream: a dashboard
  // conversation is a hidden chat (`POST /chats` with is_dashboard_chat=true) and
  // messages stream over `POST /chats/:id/messages/stream` (see chatApi + the
  // dashboard event handling in dashboardSessionApi).
  CHAT_MESSAGE_STREAM: (chatId: string) => `${baseAPI}/chats/${chatId}/messages/stream`,
  // Dashboard adoption events ingestion (batched, fire-and-forget). Backend stores
  // these in adoption_metrics (metric_category='dashboard') for the MCP analytics tool.
  DASHBOARD_METRICS_EVENTS: `${baseAPI}/dashboards/metrics/events`,
  // RBAC sharing of a single dashboard to a role or an individual user
  // (`POST`/`DELETE` with the same `{ company_id, scope, role_id?, user_id? }` body).
  DASHBOARD_SHARE: (id: string) => `${baseAPI}/dashboards/${id}/share`,
  // Reverse map dashboard_id -> its assigned use cases (read-scoped, owner+company
  // scoped on the backend; NOT admin-gated). New endpoint — older envs answer
  // 404/405/403, so callers tolerate that and render no use-case badges.
  DASHBOARD_USE_CASE_ASSIGNMENTS: `${baseAPI}/dashboards/use-case-assignments`,
  // Use-Case access model (Pulse IQ Essentials). `ME` drives the consumer nav;
  // the catalog/entitlement routes are SarasAdmin-gated on the backend (widening
  // to DashboardCreator is a tracked backend prerequisite — see the plan).
  USE_CASES: {
    // The current user's access tree (entitled use cases → shared dashboards).
    ME: `${baseAPI}/use-cases/me`,
    // A single dashboard's metadata + spec, scoped to its use case. `useCaseId`
    // is the use-case integer primary key.
    DASHBOARD: (useCaseId: number, dashboardId: string) =>
      `${baseAPI}/use-cases/${useCaseId}/dashboards/${dashboardId}`,
    // Catalog: list/create use cases.
    BASE: `${baseAPI}/use-cases`,
    // Catalog: list/assign dashboards under a use case.
    DASHBOARDS: (useCaseId: number) => `${baseAPI}/use-cases/${useCaseId}/dashboards`,
    // Catalog: assign/remove a specific dashboard from a use case.
    DASHBOARD_ASSIGN: (useCaseId: number, dashboardId: string) =>
      `${baseAPI}/use-cases/${useCaseId}/dashboards/${dashboardId}`,
    // A company's use-case entitlements (GET current / PUT to set).
    ENTITLEMENTS: (companyId: number) =>
      `${baseAPI}/use-cases/companies/${companyId}/entitlements`,
    // Shareable roles for the share modal's role picker.
    ROLES: `${baseAPI}/use-cases/roles`,
  },
  METRICS: {
    BASE_URL: baseAPI,
    USERS: `${baseAPI}/metrics/users`,
    DAILY: (startDate: string, endDate: string) => `${baseAPI}/metrics/daily?start_date=${startDate}&end_date=${endDate}`,
    DATE_RANGE: `${baseAPI}/metrics/date-range`,
    ERRORS: `${baseAPI}/metrics/errors`,
  },
  WAREHOUSES: `${baseAPI}/warehouses`,
  SAMPLES: `${baseAPI}/chats/sample`,
  FEEDBACK: `${baseAPI}/feedback`,
  CHAT_FEEDBACK: (chatId:string)=>`${baseAPI}/feedback/chat/${chatId}`,
  LATEST: (companyId: number)=>`${config.subscriptionApiUrl}/company-subscription/${companyId}/latest`,
  UTILISATION_LEVERS: (companySubscriptionId: number)=>`${config.subscriptionApiUrl}/company-subscription/utilisation-levers/${companySubscriptionId}`,
  // Levers (entitlement rules) attached to a subscription plan. The lever named
  // "USECASE" carries the use-case ids a plan is entitled to (`masterData[].masterId`).
  // Used by the Share dialog (Variant B) to scope which use cases a company can be
  // shared dashboards from. Base is the same subscription service as LATEST.
  SUBSCRIPTION_LEVERS: (planId: number)=>`${config.subscriptionApiUrl}/subscription-levers/subscription-plan/${planId}/levers`,
  // Admin: trials ending in a calendar month (`monthOffset` 0 = this month), for the
  // Subscription Management page.
  TRIALS_EXPIRING: (productName: string, monthOffset: number) =>
    `${config.subscriptionApiUrl}/admin/company-subscription/trials-expiring?${new URLSearchParams({ productName, monthOffset: String(monthOffset) })}`,
  // Admin: one company's current subscription for a product (status, plan, usage,
  // active add-ons), for the Subscription Management table rows.
  ADMIN_SUBSCRIPTION_STATUS: (productName: string, companyId: number) =>
    `${config.subscriptionApiUrl}/admin/company-subscription/status?${new URLSearchParams({ productName, companyId: String(companyId) })}`,
  // Every add-on definition across products; the "Add credits" dialog picks the IQ credit packs from it.
  ADDONS: `${config.subscriptionApiUrl}/addons`,
  // Admin subscription actions from the Subscription Management table row menu.
  SUBSCRIPTION_ADDON_PURCHASE: (companyId: number, addonId: number, quantity = 1) =>
    `${config.subscriptionApiUrl}/admin/company-subscription-addon/purchase?${new URLSearchParams({ companyId: String(companyId), addonId: String(addonId), quantity: String(quantity) })}`,
  // PUT, query params only (the endpoint reads @RequestParam, not a body); `trialExtendedTo` is "dd/MM/yyyy".
  SUBSCRIPTION_EXTEND_TRIAL: (companyId: number, productName: string, trialExtendedTo: string) =>
    `${config.subscriptionApiUrl}/admin/company-subscription/extend-trial?${new URLSearchParams({ companyId: String(companyId), productName, trialExtendedTo })}`,
  // Plans an admin can move a company onto (Saras-managed only) for one product.
  SUBSCRIPTION_PLANS: (productId: number) =>
    `${config.subscriptionApiUrl}/subscription-plan?${new URLSearchParams({ managedBySarasOnly: "true", productId: String(productId) })}`,
  // POST, query params only (the endpoint reads @RequestParam, not a body).
  SUBSCRIPTION_ASSOCIATE_MANUAL_PLAN: (companyId: number, subscriptionPlanId: number) =>
    `${config.subscriptionApiUrl}/admin/company-subscription/associate-company-with-manual-plan?${new URLSearchParams({ companyId: String(companyId), subscriptionPlanId: String(subscriptionPlanId) })}`,
  // Active add-ons on a subscription (e.g. AGENCY_USER_MANAGEMENT_DASHBOARD /
  // BRAND_USER_MANAGEMENT_DASHBOARD feature entitlements). Base is the same
  // subscription service as LATEST.
  SUBSCRIPTION_ADDONS_ACTIVE: (subscriptionId: number)=>`${config.subscriptionApiUrl}/company-subscription-addon/subscription/${subscriptionId}/active`,
  USER_SERVICE: {
    BASE: userServiceAPI,
    FIREBASE_CUSTOM_TOKEN: `${userServiceAPI}/user/firebase-custom-token`,
    VERIFY_FIREBASE_TOKEN: `${userServiceAPI}/user/verify-firebase-token`,
    USER_INFO: `${userServiceAPI}/user`,
    // All companies (id + name), for the company-entitlement picker. Returns
    // `{ data: Company[], errors: [] }`. Served ONLY by the user service (the local
    // iq-backend has no such route), so it uses the non-rerouted base — this hits
    // the real user service even under `pnpm local`.
    COMPANIES: `${rawUserServiceAPI}/company/companies`,
  },
  ADMIN: {
    CHATS: (chatId: string) => `${baseAPI}/chats/admin/${chatId}`,
    MESSAGES: (chatId: string) => `${baseAPI}/chats/admin/${chatId}/messages`,
    // Encode email to handle special characters (+, @, etc.) in URL params
    EMAIL: (emailId: string) => `${userServiceAPI}/user/userByEmail?email=${encodeURIComponent(emailId)}`,
    CHATS_BY_USER_ID: (userId: number) => `${baseAPI}/chats?user_id=${userId}`,
    FEEDBACK: `${baseAPI}/feedback/all`,
  },
  ADOPTION_METRICS: {
    // User-service IQ directory (`GET /user?productType=IQ`). Not iq-backend `/metrics/users`.
    USERS: iqUserDirectoryUrl,
    SUBSCRIPTIONS: (
      productId: string = MESSAGES.ADOPTION_METRICS.SUBSCRIPTIONS.IQ_PRODUCT_ID,
      size: string = MESSAGES.ADOPTION_METRICS.SUBSCRIPTIONS.SIZE
    ) => `${config.subscriptionApiUrl}/company-subscription/subscriptions?productId=${productId}&size=${size}`,
  },
  SLACK: {
    // Legacy IQ-only routes. The backend keeps serving these for the IQ Slack
    // app, so the live card stays on them; app-keyed routes below cover the
    // second app (`pulse` / "IQ Data Engineer").
    STATUS: `${baseAPI}/slack/status`,
    INSTALL_URL: `${baseAPI}/slack/install-url`,
    SHARE_INSTALL_URL: `${baseAPI}/slack/share-install-url`,
    DISCONNECT: `${baseAPI}/slack/disconnect`,
    // The signed-in person's OWN Slack identity, not the workspace install. GET
    // reads their link state, DELETE clears every link they hold. Both are
    // readable/writable by non-admins (`iq:user:integration:read` / `:write`),
    // which is why the personal row can call them. Linking itself only ever
    // happens in Slack — there is no website-initiated connect for it.
    LINK: `${baseAPI}/slack/link`,
    // App-keyed management routes (`app_key` in `iq | pulse`; unknown -> 404).
    // Same auth, permissions and response shapes as the legacy routes above.
    APPS: (appKey: string) => ({
      STATUS: `${baseAPI}/slack/apps/${appKey}/status`,
      INSTALL_URL: `${baseAPI}/slack/apps/${appKey}/install-url`,
      SHARE_INSTALL_URL: `${baseAPI}/slack/apps/${appKey}/share-install-url`,
      DISCONNECT: `${baseAPI}/slack/apps/${appKey}/disconnect`,
    }),
  },
  TEAMS: {
    STATUS: `${baseAPI}/teams/status`,
    CONNECTION: `${baseAPI}/teams/connection`,
    DISCONNECT: `${baseAPI}/teams/disconnect`,
    LINK: `${baseAPI}/teams/link`,
  },
  DOWNLOAD: {
    BASE: `${downloadAPI}/api/v1/download`,
    JOB_STATUS: (jobId: string) => `${downloadAPI}/api/v1/download/jobs/${jobId}`,
    HISTORY: `${downloadAPI}/api/v1/download/history`,
    PROXY: (jobId: string) => `${downloadAPI}/exports/${jobId}/download`,
  },
  SUPPORT: {
    BANNER_STATUS: `${config.supportApiUrl}/banner/status`,
  },
  RBAC: {
    // Role catalog + capability resolution. Served ONLY by the user service (the
    // local iq-backend has no `/role/all`), so it uses the non-rerouted base — this
    // hits the real user service even under `pnpm local`. Routing it through the
    // rerouted `userServiceAPI` would 404 locally, failing capability gates closed
    // ("Access restricted!" on hard loads of the manage surfaces). Mirrors COMPANIES.
    // `internal=true` is NOT "internal roles only" — the user service defaults it
    // to false and then omits the Saras-staff personas (SarasAdmin,
    // IQDashboardAdmin) from the catalog. Passing true returns the FULL set
    // (internal + customer-facing) in one call, which is what capability
    // resolution needs: without it an internal user's own role is missing from
    // `/role/all`, so `useRbac` never fetches their permissions and access
    // survives only on the `ADMIN_ROLES` blanket bypass in `hasAppPermision`.
    // User-facing role pickers must filter with `isInternalRole` (see useRbac.ts).
    role: `${rawUserServiceAPI}/role/all?productType=IQ&internal=true`,
    // Per-role permission resolution — the second leg of capability gating, fetched
    // once per role after `/role/all`. Same story as `role` above: the local
    // iq-backend has no `/permission/*`, so the rerouted `userServiceAPI` would 404
    // × N roles under `pnpm local` (→ "Not Found" toasts + gates fail closed). Use
    // the non-rerouted base so it hits the real user service even locally.
    permissions: (roleId:number)=>`${rawUserServiceAPI}/permission/role-permissions/${roleId}`,
    invitedUsers: (params: InvitedUsersParams) => {
      const queryParams = Object.keys(params).reduce((acc, key) => {
        const paramKey = key as keyof InvitedUsersParams;
        const value = params[paramKey];
        if (value !== undefined) {
          acc[paramKey] = String(value);
        }
        return acc;
      }, {} as Record<string, string>);
      return `${userServiceAPI}/company/invited-users?${new URLSearchParams(queryParams).toString()}`
    },
    inviteUser: (companyId: number) => `${userServiceAPI}/company/invites?companyId=${companyId}&productType=IQ`,
    reinviteUser: (companyId: number, userId: number) => `${userServiceAPI}/company/user/reinvite?companyId=${companyId}&userId=${userId}&productType=IQ`,
    // post
    // body: {}
    editUser: (companyId: number, userId: number) => `${userServiceAPI}/company/user/${userId}/resources?companyId=${companyId}&productType=IQ`,
    /*{
      "userId": 3902,
      "roleIds": [
          18
      ],
      "useCaseIds": [
          5,
          4,
          6,
          3,
          2
      ],
      "brandIds": [
          116
      ]
  }*/
    deleteUser: (companyId: number, userId: number) => `${userServiceAPI}/company/user/${userId}?companyId=${companyId}&productType=IQ`,
    // delete
  },
  // Data access groups + the brand catalog that feeds the group brand picker.
  // Both are served by iq-backend, so they ride `baseAPI` — unlike RBAC above,
  // which targets the user service. A user-service base would 404 under
  // `pnpm local`.
  DATA_GROUPS: {
    LIST: `${baseAPI}/data-groups`,
    DETAIL: (groupId: string) => `${baseAPI}/data-groups/${groupId}`,
    MEMBERS: (groupId: string) => `${baseAPI}/data-groups/${groupId}/members`,
    MEMBER: (groupId: string, userId: number) =>
      `${baseAPI}/data-groups/${groupId}/members/${userId}`,
  },
  BRANDS: `${baseAPI}/brands`,
  REPORTS: {
    TEMPLATES: `${baseAPI}/reportTemplates`,
    SCHEDULE: `${baseAPI}/reports/schedule`,
    PREVIEW: `${baseAPI}/reportPreview`,
    SCHEDULED_LIST: `${baseAPI}/reports/scheduled`,
    ONE_TIME_LIST: `${baseAPI}/reports/one-time`,
    RUN_HISTORY: (scheduleId: number) => `${baseAPI}/reports/${scheduleId}/runs`,
    // Same resource as SCHEDULE_DETAIL below (PATCH vs. GET) — both derive from
    // scheduleResourcePath so the two names can never silently desync.
    UPDATE_SCHEDULE: scheduleResourcePath,
    SCHEDULE_DETAIL: scheduleResourcePath,
  },
  // Company context files (business logic, defaults, sql templates, YAMLs,
  // all_tables) + the currently-live context set. The router carries no prefix,
  // so the path is literally `/files`.
  COMPANY_FILES: `${baseAPI}/files`,
  COMPANY_FILES_DOWNLOAD: `${baseAPI}/files/download`,
  // CC-B5: every live context file bundled as one ZIP.
  COMPANY_FILES_EXPORT: `${baseAPI}/files/export`,
  // Context Console. Evaluate and publish take the SAME five multipart slots;
  // evaluate writes nothing, publish is the moment a set goes live.
  CONTEXT: {
    EVALUATE: `${baseAPI}/context/evaluate`,
    PUBLISH: `${baseAPI}/context/publish`,
    QUESTIONS_VALIDATE: `${baseAPI}/context/questions/validate`,
    // Governed (eval-governance) accuracy run/poll pair — runs the question set
    // once per provider tier (primary + fallback). Replaces the old reuse of
    // the shared /evaluation job endpoint for this stage; see
    // .agents/skills/eval-governance/SKILL.md (iq-backend) and
    // app/evaluation/governance.py.
    QUESTIONS_RUN: `${baseAPI}/context/questions/run`,
    QUESTIONS_RUN_STATUS: (jobId: string) => `${baseAPI}/context/questions/run/${jobId}`,
    // CC-B4: the most recent completed run for a context_set_id — lets a
    // fresh session show run history instead of only sessionStorage.
    // Registered ahead of QUESTIONS_RUN_STATUS's {job_id} route on the
    // backend so "latest" is never captured as a job_id.
    QUESTIONS_RUN_LATEST: `${baseAPI}/context/questions/run/latest`,
    // The completed run right before a given one, for this COMPANY — not
    // scoped to a context set (every republish stamps a new one, so that
    // filter would defeat the point) — shown alongside whatever run is
    // currently on screen. Same registered-ahead-of-{job_id} contract as
    // QUESTIONS_RUN_LATEST.
    QUESTIONS_RUN_PREVIOUS: `${baseAPI}/context/questions/run/previous`,
    // CC-B3: the company's last validated/run question set.
    QUESTIONS_LATEST: `${baseAPI}/context/questions/latest`,
    // CC-B2: backfills a real score, in place, for a set published via
    // skip_evaluation — see PublishResponse.evaluation_pending.
    LIVE_SET_EVALUATE: `${baseAPI}/context/live-set/evaluate`,
    // The live set's already-published evaluation, full per-file breakdown
    // included — readable outside the tab session that ran the original
    // evaluate, which otherwise only holds that result in memory.
    LIVE_SET_EVALUATION: `${baseAPI}/context/live-set/evaluation`,
  },

};

/** User-facing copy, grouped by feature, so wording is edited in one place. */
export const MESSAGES = {
  BADGES: {
    BETA: "Beta",
  },
  USER_INSTRUCTIONS: {
    NAV_LABEL: "Instructions",
    TITLE: "Instructions",
    DESCRIPTION: "Set the guidance iQ should keep in mind when it works with you.",
    BANNER: "These preferences personalize your responses and are not applied company-wide. Once set, they apply across all iQ surfaces.",
    EYEBROW: "Personalization",
    CARD_TITLE: "How should iQ work with you?",
    CARD_DESCRIPTION: "Describe the response style, working preferences, and recurring context you want iQ to follow.",
    // The processor's generated facts: what iQ actually applies in chats, which can
    // merge or reword the text above. Always describes the SAVED version.
    FACTS_TITLE: "How iQ reads your saved instructions",
    FACTS_DESCRIPTION: "iQ turned your saved instructions into these points and applies them in new chats. Save to update them.",
    FIELD_LABEL: "Your instructions",
    PLACEHOLDER: "For example: Lead with the answer, keep recommendations concise, and ask before making assumptions.",
    HELPER: "Write clear, specific guidance. You can change this at any time.",
    COUNTER: (used: number, limit: number) => `${used.toLocaleString("en-US")} / ${limit.toLocaleString("en-US")}`,
    NEAR_LIMIT_HINT: "Close to the limit. Formatting such as bold and lists counts toward it.",
    OVER_LIMIT_HINT: (over: number) => `Remove about ${over.toLocaleString("en-US")} characters to save. Formatting counts toward the limit.`,
    SUGGESTIONS_TITLE: "Try adding a preference",
    SUGGESTIONS: [
      { id: "lead_with_answer", label: "Lead with the answer", text: "Lead with the answer, then explain the reasoning." },
      { id: "ask_before_assuming", label: "Ask before assuming", text: "Ask a clarifying question before making an assumption." },
      { id: "show_calculations", label: "Show calculations", text: "Show calculation steps for metrics and forecasts." },
    ],
    SAVE: "Save instructions",
    SAVING: "Saving…",
    DISCARD: "Discard changes",
    STATUS_UNSAVED: "Unsaved changes",
    STATUS_SAVED: "Saved just now",
    // Shown only when the save settles while no Instructions page is open.
    SAVED_TOAST: "Instructions saved",
    SAVE_FAILED_TOAST: "Your instructions weren't saved",
    // `reason` is one punctuated sentence (see saveFailureReason).
    SAVE_FAILED_TOAST_HINT: (reason: string) =>
      `${reason} Reopen Settings › Instructions in the next few minutes to pick up your text.`,
    USE_LABEL: "Use my instructions",
    USE_ENABLED_DESCRIPTION: "iQ follows your instructions in new chats.",
    USE_DISABLED_DESCRIPTION: "Your instructions are saved but iQ won't follow them until you turn this back on.",
    TOGGLE_ERROR: "Couldn't update this setting. Try again in a moment.",
    TOGGLE_UNAVAILABLE: "Changing this setting is temporarily unavailable. Try again later.",
    REPLACE_TITLE: "Replace your instructions?",
    REPLACE_DESCRIPTION: "Your saved instructions will be replaced with this version.",
    REPLACE_CONFIRM: "Replace instructions",
    REPLACE_CANCEL: "Keep editing",
    LOAD_ERROR: "Couldn't load your instructions.",
    LOADING: "Loading your instructions",
    RETRY: "Retry",
    ISSUES_TITLE: "Some of your instructions can't be saved",
    ERRORS: {
      TOO_LONG: "Your instructions are over the character limit. Shorten them and try again.",
      INVALID: "Your instructions couldn't be read. Check them and try again.",
      SECRET_DETECTED: "Your instructions look like they contain a password, API key, or other credential. Remove it and try again.",
      PROCESSOR_UNAVAILABLE: "Couldn't save right now. Try again in a moment.",
      TOO_MANY_PREFERENCES: "These instructions describe more preferences than iQ can keep. Shorten or combine them and try again.",
      SAVING_UNAVAILABLE: "Saving instructions is temporarily unavailable. Try again later.",
      NETWORK: "Couldn't reach iQ. Check your connection and try again.",
      UNKNOWN: "Couldn't save your instructions. Try again in a moment.",
    },
  },
  MEMORY: {
    SUGGESTION: {
      LABEL: "Memory suggestion",
      EYEBROW: "Memory",
      QUESTION_ONE: "Save this for future answers?",
      QUESTION_MANY: "Save these for future answers?",
      SCOPE: "Memory personalizes future iQ responses for you only and does not affect responses for other users in your company.",
      SAVE: "Save to memory",
      SAVING: "Saving…",
      DISMISS: "Don't save",
      SAVED_ONE: "Saved to memory. I'll use it in future answers.",
      SAVED_MANY: "Saved to memory. I'll use them in future answers.",
      MANAGE_MEMORIES: "Manage memories",
      EXPIRED: "This suggestion is no longer available. A newer one replaced it, or it was already answered.",
      CAPACITY_FULL: "Memory is full. Remove a memory you no longer need, then save again from a new answer.",
      RETRY: "Retry",
      RETRYING: "Retrying…",
    },
    DELETE_ACTION: "Delete memory",
    DELETE_CONFIRM_TITLE: "Delete this memory?",
    DELETE_CONFIRM_DESCRIPTION: "iQ will stop using it in future answers. Past answers and their memory history will not change.",
    DELETED_TITLE: "Memory deleted",
    DELETED_DESCRIPTION: "It will not be used in future answers.",
    DELETE_ERROR_TITLE: "Memory could not be deleted",
    DELETE_ERROR_DESCRIPTION: "Try again in a moment.",
    CANCEL: "Cancel",
    SINGULAR: "memory",
    PLURAL: "memories",
    SETTINGS: {
      TITLE: "Memories",
      DESCRIPTION: "Memory can influence iQ’s responses and may take precedence over logic in your context file. You can add or update memories by chatting with iQ, or delete them here.",
      SEARCH_PLACEHOLDER: "Search memories",
      SHARED_CONTEXT_NOTE: "If a memory—such as a metric definition or business rule—should apply to everyone, ask your Saras POC to add it to the context layer.",
      LIST_TITLE: "Your memories",
      TYPE_SINGULAR: "type",
      TYPE_PLURAL: "types",
      LOADING_LIST: "Loading memories",
      LIST_ERROR: "Memories couldn't be loaded.",
      RETRY: "Retry",
      EMPTY_TITLE: "No memories yet",
      EMPTY_DESCRIPTION: "As you correct and guide iQ, saved context will appear here.",
      NO_MATCH: (search: string) => `No memories match "${search}".`,
      DETAIL_TITLE: "Memory details",
      DETAIL_DESCRIPTION: "Review what iQ remembers and when it applies.",
      DETAIL_LOADING: "Loading memory details",
      DETAIL_ERROR: "Memory details couldn't be loaded.",
      FACTS_TITLE: "What iQ remembers",
      FACTS_EMPTY: "No remembered details are available.",
      GLOBAL_SCOPE: "Across conversations",
      CONTEXTUAL_SCOPE: "When relevant",
      UPDATED_PREFIX: "Updated",
      DELETING: "Deleting…",
      TYPE_OTHER_DESCRIPTION: "Other remembered context.",
      TYPE_OTHER_LABEL: "Other",
      USE_MEMORY_LABEL: "Use memory in conversations",
      USE_MEMORY_ENABLED_DESCRIPTION: "IQ can use and learn memories from your conversations.",
      USE_MEMORY_DISABLED_DESCRIPTION: "IQ will stop using and learning memories. Existing memories remain available to manage.",
      USE_MEMORY_TOGGLE_ERROR: "Couldn't update this setting. Try again in a moment.",
      USE_MEMORY_LOAD_ERROR: "Couldn't load this setting.",
      TYPES: {
        preference: { label: "Preference", description: "How you prefer iQ to present or analyze information." },
        correction: { label: "Correction", description: "Things you corrected so iQ does not repeat the same mistake." },
        context: { label: "Context", description: "Background about your business and recurring analysis context." },
        validation: { label: "Validation", description: "Rules you confirmed produce the correct result." },
        domain_rule: { label: "Domain rule", description: "Business definitions and calculation rules specific to your organization." },
      },
    },
    ADMIN: {
      COLUMN_LABEL: "Memory",
      TOGGLE_ARIA_LABEL: (displayName: string) => `Memory for ${displayName}`,
      TOGGLE_ERROR: "Couldn't update memory for this user. Try again in a moment.",
      LOAD_ERROR: "Couldn't load memory settings for these users.",
    },
  },
  // "Used N instructions · N memories" disclosure on a chat answer.
  APPLIED_CONTEXT: {
    TITLE: "Applied to this answer",
    SUBTITLE: "Your saved instructions and memories iQ followed here.",
    INSTRUCTIONS_LABEL: "Instructions",
    MEMORIES_LABEL: "Memories",
    EDIT_INSTRUCTIONS: "Edit",
    MANAGE_MEMORIES: "Manage",
    FOOTER: "iQ reports which items it applied.",
    USED_PREFIX: "Used",
    COUNT_SEPARATOR: "·",
    INSTRUCTION_COUNT: (count: number) => (count === 1 ? "instruction" : "instructions"),
    MEMORY_COUNT: (count: number) => (count === 1 ? "memory" : "memories"),
    TRIGGER_ARIA_LABEL: (summary: string) => `Show what this answer used: ${summary}`,
    SUMMARY_JOINER: " and ",
    NO_LONGER_SAVED: "No longer in your memories.",
    LOADING_MEMORY: "Loading memory",
    LOAD_ERROR: "Some memories couldn't be loaded.",
    RETRY: "Retry",
    DELETE_ARIA_LABEL: (name: string) => `Delete memory: ${name}`,
    CONFIRM_DELETE: (name: string) => `Delete “${name}”? iQ won’t use it in future answers.`,
    CONFIRM_GROUP_LABEL: "Confirm delete",
    DELETE: "Delete",
    DELETING: "Deleting…",
  },
  TEAMS: {
    CONNECTION_SUCCESS: "Teams connection successful!",
    CONNECTION_FAILED: "Failed to connect Teams. Please try again.",
    DISCONNECT_SUCCESS: "Teams disconnected successfully",
    DISCONNECT_FAILED: "Failed to disconnect Teams",
    STATUS_LOAD_FAILED: "Failed to load Teams connection status",
    CLAIM_CODE_REQUIRED: "Please enter the claim code",
    CONNECT_WORKSPACE: "Already installed? Connect workspace",
    DISCONNECT: "Disconnect",
    ADD_FROM_STORE: "Add from Teams Store",
    PILOT_INSTALL_NOTE: "Your Teams admin installs the Saras IQ app from the package we share. Then open the bot in Teams and ask a question.",
    INSTALL_GUIDE_LINK: "Read the install guide",
    TENANT_NAME_LABEL: "Microsoft tenant name (optional)",
    TENANT_NAME_PLACEHOLDER: "e.g. Contoso",
    // The signed-in person's OWN Teams account (Settings > Integrations, the
    // sub-row under the Teams card). Linking happens in Teams — the bot asks the
    // person to sign in — so there is deliberately no connect copy here, only
    // state, a shortcut into Teams, and the unlink path.
    USER_LINK: {
      // Names the account in every state, so "not linked" never reads as the
      // person's iQ account.
      EYEBROW: "Your Teams account",
      // Rendered as sr-only text inside the skeleton, so the live region around
      // the row announces more than an empty box while the link state resolves.
      LOADING: "Loading your Teams link",
      // The not-linked status line; sits where the linked person's name sits.
      NOT_LINKED_TITLE: "Not linked yet",
      // The muted line under it: what to do in Teams to link.
      NOT_LINKED: "Ask iQ a question in Teams to sign in.",
      // Not-linked shortcut to the bot chat in Teams; navigation only, linking
      // still happens when the person asks the bot a question.
      OPEN_TEAMS: "Open in Teams",
      // Accessible name of that link; keeps the visible "Open in Teams" inside
      // it (WCAG 2.5.3, label in name) and says it leaves the app.
      OPEN_TEAMS_ARIA_LABEL: "Open in Teams, new tab",
      LOAD_FAILED: "Could not load your Teams link.",
      RETRY: "Retry",
      UNLINK: "Unlink",
      // Accessible name of the row's Unlink button; "Unlink" alone says not what.
      UNLINK_ARIA_LABEL: "Unlink your Teams account",
      LINKED_SINCE: (date: string) => `Linked since ${date}`,
      // Tenant part of the linked line, labelled so a bare company name does
      // not read as the person's name.
      TENANT: (name: string) => `Tenant: ${name}`,
      // Shown when the backend knows no name or email for a link.
      UNKNOWN_ACCOUNT: "Teams account",
      UNLINK_TITLE: "Unlink your Teams account",
      // One Unlink clears every link, so with several the title says so.
      UNLINK_TITLE_MANY: (count: number) => `Unlink all ${count} Teams accounts`,
      UNLINK_DESC: "Your next question in Teams will ask you to sign in again.",
      CANCEL: "Cancel",
      UNLINKED: "Teams account unlinked",
      UNLINK_FAILED: "Could not unlink your Teams account. Please try again.",
      // Under a link whose tenant is not the one this company connected (only
      // admins know the connected tenant id, so only they see it).
      LINKED_OTHER_COMPANY: "This Teams account is linked under another company.",
    },
  },
  USER_MANAGEMENT: {
    // Shared by the Users table (UserManagementTable) and the Groups table
    // (ManageGroupTable) — same wording for "your search matched nothing" in
    // both RBAC surfaces.
    NO_SEARCH_RESULTS: "Oops! we couldn't find any results for your search",
    // Success toasts for user and group admin actions.
    USER_DELETED: "User deleted successfully",
    INVITES_SENT: "Invite(s) sent successfully",
    ROLE_UPDATED: "Role updated successfully",
    GROUP_CREATED: "Group created successfully",
    GROUP_UPDATED: "Group updated successfully",
    GROUP_DELETED: "Group deleted successfully",
    GROUP_MEMBERS_UPDATED: "Group members updated successfully",
    // Membership saves own their error toast (the requests skip the global
    // handler), so this copy is the whole message the admin sees.
    // Used when a failed request carries no backend message of its own.
    REQUEST_FAILED_REASON: "Something went wrong",
    MOVE_FAILED_TITLE: "Couldn't move this user",
    MOVE_ROLLED_BACK: (reason: string) => `${reason}. Nothing was changed — try again.`,
    MOVED_INTO_BOTH_GROUPS_TITLE: "This user is now in both groups",
    MOVED_INTO_BOTH_GROUPS_DESCRIPTION:
      "The move failed and the automatic undo also failed. Retry, or remove one of the two group memberships manually.",
    REMOVE_FROM_GROUP_FAILED_TITLE: "Couldn't remove this user from the group",
    TRY_AGAIN: (reason: string) => `${reason}. Try again.`,
    MEMBERS_UPDATE_FAILED_TITLE: (count: number) =>
      count === 1 ? "1 user couldn't be updated" : `${count} users couldn't be updated`,
    MEMBERS_UPDATE_FAILED_DESCRIPTION: (reason: string) =>
      `${reason}. The marked rows weren't saved — try again.`,
  },
  ERRORS: {
    AUTH_FAILED: "Authentication failed. Please try again.",
    TOKEN_EXPIRED: "Your session has expired. Please login again.",
    INVALID_TOKEN: "Invalid authentication token.",
    INVITE_USER: "Failed to send invite(s)",
  },
  SUCCESS: {
    LOGIN: "Successfully logged in.",
    LOGOUT: "Successfully logged out.",
    INVITE_USER: "Invite(s) sent successfully",
  },
  ERROR_CODES: {
    USER_EXISTS: "US406",
    NETWORK: "NW-0000",
    SERVER_ERROR: "SV-5XX",
    UNKNOWN_ERROR: "UNKNOWN_ERROR",
    ACCESS_DENIED: "PS404012",
    CHAT_CANCELLED: "CHAT_CANCELLED",
    // iq-backend chat gate: wizard not complete or readiness not yet `ready`.
    ONBOARDING_NOT_READY: "ONBOARDING_NOT_READY",
    CHAT_SHARE_NO_ACTIVE_LINKS: "CHAT_SHARE_NO_ACTIVE_LINKS",
  },
  PRODUCT_ACCESS_DENIED: {
    TITLE_LEAD: "You don't have access to",
    PRODUCT_LABEL: "Saras iQ",
    BODY: "No worries, reach out to your org admin to request access to this app.",
    CONTACT_LEAD: "If you're facing any issues, feel free to",
    CONTACT: "Contact Us",
    SIGN_IN_ANOTHER: "Sign in with another account",
    BACK_TO_APPS: "Back to Saras apps",
  },
  // A user who has iQ but not one admin section. Not the product-level screen:
  // no sign-out, no "Back to Saras apps".
  SECTION_ACCESS_DENIED: {
    TITLE: "You don't have access to this section",
    BODY: "Reach out to your org admin to request access.",
  },
  // /auth screen after the user chose Sign out in this tab: no SSO bounce back
  // through accounts until they click Sign in.
  SIGNED_OUT: {
    TITLE: "Signed out",
    BODY: "You are signed out of iQ. Sign in again to continue.",
    ACTION: "Sign in",
  },
  SUBSCRIPTION_MANAGEMENT: {
    TITLE: "Subscription Management",
    SEARCH_TRIGGER: "Search company",
    SEARCH_PLACEHOLDER: "Search company by name...",
    SEARCH_EMPTY: "No company found.",
    COMPANY_ID: "Company ID",
    UNNAMED_COMPANY: (id: number) => `Company #${id}`,
    NO_SUBSCRIPTION: "No IQ subscription on record.",
    ROW_ERROR: "Could not load this subscription.",
    RETRY: "Try again",
    NA: "—",
    CLEAR_SELECTION: "Clear",
    DETAILS_HEADING: "Subscription details",
    TRIALS: {
      HEADING: "Trials expiring",
      PLAN_EXPIRING_LABEL: "Plan expiring",
      MONTH_OPTIONS: [
        { value: 0, label: "This month" },
        { value: 1, label: "Next month" },
        { value: 2, label: "In 2 months" },
      ],
      COUNT: (n: number) => `${n} ${n === 1 ? "company" : "companies"}`,
      EMPTY_TITLE: "No trials expiring",
      EMPTY_DESCRIPTION: "No IQ trials end in this month.",
      ERROR_TITLE: "Could not load expiring trials",
      ERROR_DESCRIPTION: "The subscription service did not respond. Try again in a moment.",
    },
    COLUMNS: {
      COMPANY: "Company",
      PRODUCT: "Product",
      PLAN: "Plan",
      STATUS: "Status",
      NEXT_BILLING: "Next billing",
      TRIAL_END: "Trial ends",
      USAGE: "Credits used",
      ADD_ONS: "Active add-ons",
      ACTIONS: "Actions",
    },
    ACTIONS: {
      TRIGGER: (company: string) => `Subscription actions for ${company}`,
      PENDING: (company: string) => `Updating subscription for ${company}`,
      ADD_CREDITS: "Add credits",
      EXTEND_TRIAL: "Extend trial",
      CHANGE_SUBSCRIPTION: "Change subscription",
    },
    DIALOGS: {
      CANCEL: "Cancel",
      ADD_CREDITS: {
        TITLE: "Add credits",
        DESCRIPTION: (company: string) => `Choose a credit pack to add to ${company}.`,
        PACK_LABEL: (credits: number) => `${credits.toLocaleString("en-US")} credits`,
        PACKS_ERROR: "Could not load credit packs. Try again in a moment.",
        PACKS_EMPTY: "No credit packs are set up for IQ.",
        PACK_GROUP: "Credit pack",
        CONFIRM: "Add credits",
        SUCCESS: (credits: number, company: string) =>
          `Added ${credits.toLocaleString("en-US")} credits to ${company}`,
      },
      EXTEND_TRIAL: {
        TITLE: "Extend trial",
        DESCRIPTION: (company: string) => `Pick the new trial end date for ${company}.`,
        CURRENT_END: "Current trial end",
        NEW_END: "New trial end",
        PICK_DATE: "Select date",
        CONFIRM: "Extend trial",
        SUCCESS: (company: string, date: string) => `Extended ${company}'s trial to ${date}`,
      },
      CHANGE_SUBSCRIPTION: {
        TITLE: "Change subscription",
        DESCRIPTION: "Pick the plan to move this company onto.",
        COMPANY: "Company",
        CURRENT_STATUS: "Current status",
        CURRENT_PLAN: "Current plan",
        NEW_PLAN: "New plan",
        NO_PLANS: "No other plans are available for this product.",
        PLANS_ERROR: "Could not load the available plans.",
        NO_PRODUCT: "Plans can't be listed: no IQ add-on is set up in the subscription service to read the IQ product from.",
        CONFIRM: "Change plan",
        SUCCESS: (company: string, plan: string) => `${company} is now on ${plan}`,
      },
    },
  },
  SERVICE_STATUS: {
    TITLE: "iQ will return soon",
    DESCRIPTION: "We're resolving a system-wide issue. Please try after sometime or contact our support team at",
    SUPPORT_EMAIL: "support@sarasanalytics.com",
    REACH_OUT_NOTE: "Reaching out to support? Mention this",
    DOWN_STATUS_SUFFIX: "is down",
    DOWN_STATUS_SUFFIX_PLURAL: "are down",
    DEFAULT_SERVICE_NAME: "Service",
  },
  APP_UPDATE: {
    // Nav-rail card.
    TITLE: "Update Available",
    SUBTITLE: "Click here to load the latest version",
    // Explicit popup — a more prominent call-out than the nav card. Closing it
    // (X) leaves the card in the rail until the user updates.
    POPUP_TITLE: "Update Available",
    POPUP_DESC: "Refresh your browser to load the latest version of iQ.",
    POPUP_CTA: "Refresh Now",
    // Chat-processing edge case: applying mid-response would drop the answer.
    PROCESSING_TITLE: "A chat is processing",
    PROCESSING_DESC: "Reloading now will stop the current response.",
    PROCESSING_CTA: "Reload Now",
    // Secondary in processing mode — stay on the current build and let the
    // answer finish; the nav card keeps the update reachable afterwards.
    PROCESSING_CANCEL: "Keep waiting",
    // Failed state: the waiting worker never took control. Say so, stay clickable.
    FAILED_TITLE: "Update didn't finish",
    FAILED_SUBTITLE: "Try again",
    // Same failure, said in the popup (which stays open through the apply).
    POPUP_FAILED_DESC: "The new version didn't load. Try again, or close all iQ tabs and reopen.",
  },
  SLACK: {
    INTEGRATE_BUTTON: "Integrate with Slack",
    REDIRECTING: "Redirecting...",
    CONNECT_DIALOG: {
      TITLE: "Connect your Slack workspace",
      SUBTITLE: "What's your role in managing the Slack workspace?",
      NOT_ADMIN_LABEL: "I'm not the Slack admin",
      // Templated on the app name so the same dialog serves every Slack app
      // (iQ, IQ Data Engineer). Title/subtitle/labels stay Slack-generic.
      NOT_ADMIN_DESC: (appName: string) =>
        `Share the link below with your Slack admin to approve ${appName} access for your Slack workspace`,
      ADMIN_LABEL: "I'm the Slack admin",
      ADMIN_DESC: "You'll be taken to Slack to approve the connection",
      LINK_LABEL: "Admin approval link",
      LINK_EXPIRY: "(This link will expire in 48 hours)",
      // A sentence fragment: follows `LINK_LABEL` in the dialog and "This link"
      // in the "copied" toast once `expires_at` from `GET .../share-install-url`
      // is known. Never added to the copied text: extra lines break a paste into
      // the address bar.
      LINK_EXPIRES_AT: (when: string) => `works until ${when}`,
      COPY_LINK: "Copy link",
      LINK_COPIED: "Link copied!",
      CANCEL: "Cancel",
      CONTINUE_SETUP: "Continue Setup",
      COPY_FAILED: "Failed to copy link. Please try again.",
      LINK_FETCH_FAILED: "Failed to generate install link. Please try again.",
      GENERATING_LINK: "Generating link...",
    },
    // Per-app copy for the Settings > Integrations cards. `IQ` holds the strings
    // the Slack card has always shown (moved verbatim, no visible change);
    // `PULSE` is the second Slack app, labelled "IQ Data Engineer" in the UI
    // while the backend `app_key` stays "pulse".
    APPS: {
      IQ: {
        DIALOG_NAME: "iQ",
        CONNECTED: "Integrated with Slack successfully",
        CONNECT_FAILED: "Failed to connect Slack. Please try again.",
        DISCONNECTING: "Disconnecting Slack...",
        DISCONNECTED: "Slack disconnected",
        DISCONNECT_FAILED: "Failed to disconnect Slack. Please try again.",
        DISCONNECT_TITLE: "Disconnect Slack",
        DISCONNECT_DESC:
          "Are you sure you want to disconnect Slack? Your team will no longer be able to use the Saras iQ bot in Slack.",
      },
      PULSE: {
        DIALOG_NAME: "iQ Data Engineer",
        CONNECTED: "Integrated with iQ Data Engineer successfully",
        CONNECT_FAILED: "Failed to connect iQ Data Engineer. Please try again.",
        DISCONNECTING: "Disconnecting iQ Data Engineer...",
        DISCONNECTED: "iQ Data Engineer disconnected",
        DISCONNECT_FAILED: "Failed to disconnect iQ Data Engineer. Please try again.",
        DISCONNECT_TITLE: "Disconnect iQ Data Engineer",
        DISCONNECT_DESC:
          "Are you sure you want to disconnect iQ Data Engineer? Your team will no longer be able to use the Saras data engineering agent in Slack.",
      },
    },
    // Inline banner for a rejected Slack OAuth return, keyed by the backend's
    // `?reason=` (`failed` and unknown reasons use the app's CONNECT_FAILED).
    // `expired` is only sent for share links — one past its 48 hours, or one
    // replaced by a newer share link; the 5-minute direct link sends
    // `session_expired`. The button is "Connect" or a "Reconnect" menu item
    // depending on state, so the copy never names it.
    STATUS_PAGE: {
      TITLE_SUCCESS: "Connection Successful",
      TITLE_EXPIRED: "Connect Link Expired",
      TITLE_SESSION_EXPIRED: "Connection Timed Out",
      TITLE_INVALID: "Invalid Connect Link",
      TITLE_CANCELLED: "Authorization Cancelled",
      TITLE_FAILED: "Connection Failed",
      TITLE_UNKNOWN: "Slack Connection",
      APP_NAMES: {
        iq: "Saras iQ",
        pulse: "iQ Data Engineer",
      },
      SUCCESS_DESC: (appName: string) =>
        `${appName} is now connected to your Slack workspace. You may safely close this window.`,
      EXPIRED_DESC:
        "This connect link has expired or was replaced by a newer one. Shared admin links are valid for 48 hours. Please ask your iQ team to generate a new link.",
      SESSION_EXPIRED_DESC:
        "The Slack authorization session timed out. Please ask your iQ team to start the connection again.",
      INVALID_DESC:
        "This connect link is no longer valid or has already been used. Please ask your iQ team for a new link.",
      CANCELLED_DESC:
        "Slack authorization was cancelled. You may safely close this window, or contact your iQ team if you still need this app connected.",
      FAILED_DESC: (appName: string) =>
        `We could not complete the connection to ${appName}. Please ask your iQ team for assistance.`,
      GENERIC_DESC: "No active connection details were found. You may safely close this window.",
      CLOSE_WINDOW: "Close window",
      GO_TO_INTEGRATIONS: "Go to Integrations",
    },
    CONNECT_ERROR: {
      DISMISS: "Dismiss",
      expired: {
        CAN_MANAGE:
          "This connect link has expired or was replaced by a newer one. Shared links work for 48 hours. Start the connection again from this page.",
        READ_ONLY: "This connect link has expired or was replaced by a newer one. Ask your iQ admin for a new link.",
      },
      session_expired: {
        CAN_MANAGE: "The Slack connection took too long to finish. Start the connection again from this page.",
        READ_ONLY: "The Slack connection took too long to finish. Ask your iQ admin to start it again.",
      },
      invalid: {
        CAN_MANAGE:
          "This connect link is no longer valid. It may have been used already. Start the connection again from this page.",
        READ_ONLY: "This connect link is no longer valid. Ask your iQ admin for a new link.",
      },
      cancelled: {
        CAN_MANAGE: "Slack authorization was cancelled. Start the connection again whenever you're ready.",
        READ_ONLY: "Slack authorization was cancelled. Ask your iQ admin if you still need this connected.",
      },
    },
    // Settings > Integrations row/action strings shared by every Slack-app card.
    // They live on `IntegrationCard` (the Slack connect/disconnect variant of
    // `IntegrationRow`), so they belong to SLACK rather than a generic block.
    INTEGRATION_CARD: {
      CONNECT: "Connect",
      CONNECTED: "Connected",
      MANAGE: "Manage Slack integration",
      RECONNECT: "Reconnect",
      DISCONNECT: "Disconnect",
      READ_ONLY_HINT: "Ask an iQ admin to manage this integration.",
      SHARE_LINK: "Not a Slack admin? Share link",
      SHARE_LINK_COPYING: "Copying...",
      SHARE_LINK_COPIED_TITLE: "Install link copied!",
      SHARE_LINK_COPIED_DESC: "Share it with your Slack admin. Link expires in 48 hours.",
      SHARE_LINK_COPIED_DESC_UNTIL: (expiryLine: string) => `Share it with your Slack admin. This link ${expiryLine}.`,
      SHARE_LINK_GENERATED_TITLE: "Install link generated!",
      SHARE_LINK_GENERATED_DESC: 'Auto-copy failed. Click "Share link" again to retry.',
    },
    // The signed-in person's OWN Slack identity (Settings > Integrations, the
    // sub-row under the IQ Slack card). Linking happens in Slack — the bot sends
    // a sign-in button — so there is deliberately no connect copy here, only
    // state, a shortcut into Slack, and the unlink path.
    USER_LINK: {
      // Names the account in every state, so "not linked" never reads as the
      // person's iQ account.
      EYEBROW: "Your Slack account",
      // Rendered as sr-only text inside the skeleton, so the live region around
      // the row announces more than an empty box while the link state resolves.
      LOADING: "Loading your Slack link",
      // The not-linked status line; sits where the linked person's name sits.
      NOT_LINKED_TITLE: "Not linked yet",
      // The muted line under it: what to do in Slack to link.
      NOT_LINKED: "Ask iQ a question in Slack to sign in.",
      // Not-linked shortcut to the workspace in Slack; navigation only, linking
      // still happens from the bot's sign-in button.
      OPEN_SLACK: "Open Slack",
      // Accessible name of that link; says it leaves the app.
      OPEN_SLACK_ARIA_LABEL: "Open Slack in a new tab",
      LOAD_FAILED: "Could not load your Slack link.",
      RETRY: "Retry",
      UNLINK: "Unlink",
      // Accessible name of the row's Unlink button; "Unlink" alone says not what.
      UNLINK_ARIA_LABEL: "Unlink your Slack account",
      LINKED_SINCE: (date: string) => `Linked since ${date}`,
      // Workspace part of the linked line; a bare name like "iQ" reads as the bot.
      WORKSPACE: (name: string) => `${name} workspace`,
      // Shown when the backend knows no name, email or id for a link.
      UNKNOWN_ACCOUNT: "Slack account",
      UNLINK_TITLE: "Unlink your Slack account",
      // One Unlink clears every link, so with several the title says so.
      UNLINK_TITLE_MANY: (count: number) => `Unlink all ${count} Slack accounts`,
      UNLINK_DESC: "Your next question in Slack will ask you to sign in again.",
      CANCEL: "Cancel",
      UNLINKED: "Slack account unlinked",
      UNLINK_FAILED: "Could not unlink your Slack account. Please try again.",
    },
  },
  ADOPTION_METRICS: {
    DASHBOARD_TITLE: "iQ Analytics",
    GO_BACK: "Go Back",
    SEARCH_PLACEHOLDER: "Search companies...",
    SEARCH_USERS_PLACEHOLDER: "Search users...",
    ALL_COMPANIES: "All Companies",
    NA: "N/A",
    STATUS: {
      ACTIVE: "Active",
      TRIAL: "Trial",
      CANCELLED: "Cancelled",
      TRIAL_ENDED: "Trial Ended",
    },
    TABS: {
      USERS_ACTIVITY: "Users & Activity",
      SUBSCRIPTIONS: "Subscriptions",
      ERRORS: "Errors",
    },
    USERS: {
      TITLE: "Users & Activity",
      DESCRIPTION: "User engagement and daily active users",
      TOTAL_REGISTERED: "Total Registered Active Users",
      ACROSS_ALL_COMPANIES: "Across all companies",
      AVG_PER_COMPANY: "Avg Users per Company",
      COMPANIES_TOTAL: "companies total",
      ACTIVE_USERS: "Active Users",
      VERIFIED_USERS: "Verified logged in users",
      USER_DETAILS: "User Details",
      SHOWING_USERS: "Showing {count} users",
      DAILY_ACTIVITY: "Daily Activity",
      ERROR_LOADING_DAILY_METRICS: "Error loading daily metrics",
      OVERALL_USAGE_ACCUMULATED_FROM: "Overall usage accumulated from",
      OVERALL_USAGE_ACCUMULATED_OVER_SELECTED_PERIOD: "Overall usage accumulated over the selected period",
      COUNT: "Count",
    },
    SUBSCRIPTIONS: {
      TITLE: "Subscription Management",
      DESCRIPTION: "Company subscriptions and billing details",
      BY_STATUS: "Companies by Subscription Status",
      TOTAL_COMPANIES: "Total of {count} companies",
      SUBSCRIPTION_DETAILS: "Subscription Details by Company",
      SHOWING_COMPANIES: "Showing {count} companies",
      IQ_PRODUCT_ID: "3",
      SIZE: "1000"
    },
    METRICS: {
      TOTAL_COMPANIES: "Total Companies",
      ACTIVE_SUBSCRIPTIONS: "Active Subscriptions",
      TRIAL_CONVERSION_RATE: "Trial Conversion Rate",
      MONTHLY_CHURN_RATE: "Monthly Churn Rate",
      AVG_CREDITS_USED: "Avg Credits Used",
      TRIALS_TO_PAID: "Converted from Trials",
      CANCELLED_SUBSCRIPTIONS: "Cancelled subscriptions",
      PER_COMPANY_CYCLE: "Per company this cycle",
      PAYING_COMPANIES: "Paying companies",
    },
    FEEDBACK: {
      TRENDS: "Feedback Trends",
      DAILY_ACTIVITY: "Daily feedback activity over time",
      ERROR_LOADING: "Error loading feedback data",
      DATE: "Date",
      COUNT: "Feedback Count",
    },
    ERRORS_TAB: {
      TITLE: "Errors",
      DESCRIPTION: "Conversation error trends over time",
      CHART_TITLE: "Daily Error Trends",
      CHART_DESCRIPTION: "Errors and total conversations per day",
      ERROR_LOADING: "Error loading error metrics",
      DATE: "Date",
      COUNT: "Count",
      TOTAL_ERRORS: "Total Error Conversations",
      TOTAL_CONVERSATIONS: "Total Conversations",
      ERROR_RATE: "Error Rate",
      NO_OF_CONVERSATIONS_FOR_SELECTED_PERIOD: "No of Conversations for selected period",
      NO_OF_CONVERSATIONS_WITH_ERRORS: "No of Conversations with Errors",
      ERRORS_PER_CONVERSATION: "Errors per conversation",
    },
    FILTERS: {
      USER_FILTER: "User Filter",
      COMPANY_FILTER: "Company Filter",
      EXCLUDE_SARAS_USERS: "Exclude Saras Users",
      INCLUDE_ALL_USERS: "Include All Users",
      EXCLUDE_SARAS_ANALYTICS: "Exclude Saras Analytics",
      INCLUDE_ALL_COMPANIES: "Include All Companies",
      ALL_STATUSES: "All Statuses",
    },
    TABLE: {
      USER_ID: "User ID",
      EMAIL: "Email",
      COMPANY: "Company",
      ROLE: "Role",
      STATUS: "Status",
      CHATS: "Chats",
      MESSAGES: "Messages",
      COMPANY_ID: "Company ID",
      COMPANY_NAME: "Company Name",
      CREDITS_USED: "Credits Used",
      CREDITS_AVAILABLE: "Credits Available",
      TRIAL_PERIOD: "Trial Period",
      SUBSCRIPTION_PERIOD: "Subscription Period",
    },
  },
  SIDEBAR: {
    COLLAPSE: "Collapse sidebar",
  },
  REPORTS: {
    NAV_LABEL: "Reports",
  },
  CONTEXT_CONSOLE: {
    NAV_LABEL: "Context Console",
    // The settings-nav dot for unviewed activity (a completed evaluate,
    // backfill or accuracy run) — announced for screen readers, which never
    // see an aria-hidden dot on its own.
    NAV_UNVIEWED_LABEL: "New results",
    CRUMB: "Context Console",
    PAGE_DESCRIPTION:
      "Context Console gives iQ the brand's metric definitions, calculation rules, and data relationships. Upload and publish these files so iQ can answer analytics questions using the brand's business logic.",
    // Header status badge, next to the client name — mirrors the mockup's
    // setupStatus logic (new/editing/published), not a separate live-set chip.
    STATUS_NEW: "New context setup",
    STATUS_EDITING: "Editing existing context",
    STATUS_PUBLISHED: "Context published",
    // CC-C2: only rendered when config.contextConsoleResourcesLink is set.
    RESOURCES_LINK: "Resources",
    LIVE_SET_TRIGGER: (date: string) => `Live context · ${date}`,
    LIVE_SET_POPOVER_TITLE: "Context currently used by iQ",
    LIVE_SET_ID_LABEL: "Set ID",
    // Value is a pre-formatted date+time+tz string (formatDateTimeWithTz),
    // not the raw ISO timestamp — organised as its own labelled row so the
    // moment it was published reads clearly, timezone included.
    LIVE_SET_PUBLISHED_LABEL: "Published",
    LIVE_SET_EXPLAINER: "New files only replace this context when you publish.",
    // CC-B2: skip-evaluation publish, local/dev/test only (the backend
    // 403s everywhere else regardless of this control's visibility).
    PENDING_BADGE: "Score pending",
    PENDING_BADGE_TITLE: "Published without evaluating — no score yet",
    RUN_EVALUATION_NOW: "Run evaluation now",
    // Shown once, right after any of the three publish paths succeeds — an
    // explicit acknowledgement, separate from the below-threshold override
    // confirm dialog (EVALUATE.OVERRIDE_GATE_*).
    PUBLISH_NOTICE_TITLE: "Context files updated",
    PUBLISH_NOTICE_BODY: "Your context files were published successfully.",
    PUBLISH_NOTICE_OK: "OK",
    // Gates every publish click (all three paths) — confirms the admin
    // means it, and requires a real reason. Validated by
    // utils/publishReason.ts's validatePublishReason before this ever
    // reaches the server, which re-checks it authoritatively.
    CONFIRM_PUBLISH_TITLE: "Publish this context?",
    CONFIRM_PUBLISH_BODY:
      "Publishing will add/update all context files in the database. These changes will affect how iQ answers questions for all users in this company. Make sure you're confident in the changes before publishing.",
    CONFIRM_PUBLISH_REASON_LABEL: "Reason for this update",
    CONFIRM_PUBLISH_REASON_PLACEHOLDER:
      "e.g. Added Q3 pricing rules and fixed the stale revenue metric definition",
    CONFIRM_PUBLISH_CANCEL: "Cancel",
    CONFIRM_PUBLISH_CONFIRM: "Publish",
    // utils/publishReason.ts's validatePublishReason — same rules, same
    // order as app/api/context_console.py's _validate_publish_reason on the
    // backend, which re-checks this authoritatively (the client copy here is
    // instant feedback only).
    REASON_REQUIRED: "A reason is required.",
    REASON_JUNK: "That doesn't look like a real reason — describe what changed and why.",
    REASON_TOO_SHORT: (min: number) => `Add a bit more detail — at least ${min} characters.`,
    REASON_TOO_LONG: (max: number) => `Keep it to ${max} characters or fewer.`,
    REASON_TOO_FEW_WORDS: (min: number) => `Describe what changed and why, in at least ${min} words.`,
    REASON_REPETITIVE: "That looks repetitive — describe what actually changed.",
    EVALUATING_LIVE_SET: "Scoring…",
    EVALUATION_BACKFILLED_TITLE: "Live set scored",
    EVALUATION_BACKFILLED_BODY: (combined: number) => `Combined score: ${combined}%.`,
    EVALUATION_BACKFILL_FAILED_TITLE: "Scoring failed",
    EVALUATION_BACKFILL_FAILED: "Couldn't score the live set. Try again.",
    // CC-B5: every live context file bundled as one ZIP.
    EXPORT_FILENAME: (setId: string) => `${setId}.zip`,
    EXPORT_FAILED_TITLE: "Download failed",
    EXPORT_FAILED: "Couldn't download the live set. Try again.",
    // Copy for the persistent "finished while you were elsewhere" notifications
    // — one for Test & Run (EvaluationJobContext), one for file evaluation
    // (FileEvaluationContext), one for publish (PublishContext). Both fire the
    // same toast/nav-badge machinery; only the wording differs. PUBLISH's
    // failure copy deliberately reuses EVALUATE.PUBLISH_FAILED_TITLE/BODY
    // below rather than duplicating it — same message, two surfaces (inline
    // toast when still on the page, this notification when not).
    NOTIFY: {
      VIEW: "View results",
      RUN: {
        TITLE: "Accuracy run complete",
        BODY: (correct: number, total: number) => `${correct} of ${total} answers matched.`,
        FAILED_TITLE: "Accuracy run failed",
        FAILED_BODY: "Check the question set and try again.",
      },
      EVALUATE: {
        TITLE: "Context evaluation complete",
        BODY: (combined: number) => `Combined score: ${combined}%.`,
        FAILED_TITLE: "Context evaluation failed",
        FAILED_BODY: "Check the uploaded files and try again.",
      },
      PUBLISH: {
        VIEW: "View receipt",
        TITLE: "Context published",
        BODY: "New version is now live.",
      },
    },
    // The five inputs. Order is the contract's fixed order; `key` matches both the
    // multipart slot name and the `key` on each returned score row. `label` +
    // `format` render as "N. {label} ({format})", mirroring the mockup's numbered
    // rows — `label` alone (no format suffix) is what MISSING_WARNING joins.
    SLOTS: [
      {
        key: "business_logic",
        label: "Business logic",
        format: "(.md)",
        hint: "Define metrics, trusted tables, exclusions, and how to resolve conflicting numbers so iQ follows your business logic.",
        accept: ".md,.markdown,.txt",
      },
      {
        key: "defaults",
        label: "Default values",
        format: "(.json)",
        hint: "Set the time period, currency, filters, and other defaults iQ uses when a question leaves details out.",
        accept: ".json",
      },
      {
        key: "sql_templates",
        label: "SQL query templates",
        format: "(.json)",
        hint: "Provide verified SQL patterns for common questions. iQ fills in the details from each question.",
        accept: ".json",
      },
      {
        key: "yamls",
        label: "Individual table YAML file(s)",
        format: "(.yml)",
        hint: "Describe column types, meanings, and accepted values for each table listed in the all-tables file.",
        // .zip too: the backend auto-expands a zip dropped in this slot
        // (company_files_store.expand_zips) — the picker's own filter was
        // silently blocking that already-supported path.
        accept: ".yml,.yaml,.zip",
      },
      {
        key: "all_tables",
        label: "All-tables YAML file",
        format: "(.yml)",
        hint: "Describe what each table contains, how often it refreshes, and what one row represents so iQ can choose the right table.",
        accept: ".yml,.yaml",
      },
    ],
    STEPS: [
      { stage: "upload", label: "Add context", sub: "5 context inputs" },
      { stage: "evaluate", label: "Evaluate & publish", sub: "Scores and gate" },
      { stage: "test", label: "Validation", sub: "Validate and accuracy" },
    ],
    UPLOAD: {
      TITLE: "Add context files",
      DESCRIPTION:
        "Upload business logic, defaults, SQL templates, and table descriptions and schemas to guide iQ's answers. All five file types are required.",
      // Shown instead of DESCRIPTION once a live set already exists — the
      // five rows below are then pre-filled with what's live, not blank.
      DESCRIPTION_EDITING:
        "Each row shows the file that's live right now. Replace only the ones you're changing — anything left alone stays exactly as it is when you evaluate or publish.",
      // CC-C1: blank starter files for all 5 inputs, for a company with
      // nothing to start from yet.
      DOWNLOAD_TEMPLATES: "Download templates ZIP",
      // Shown only once a live set exists — a snapshot of what's actually
      // live right now, distinct from the (always-blank) starter templates.
      DOWNLOAD_PUBLISHED: "Download published files ZIP",
      COUNT: (n: number) => `${n} of 5 input types added`,
      YAML_COUNT: (n: number) => `${n} YAML files`,
      CHOOSE: "Upload file",
      CHOOSE_MULTI: "Upload files",
      REPLACE: "Replace file",
      FILE_COUNT: (n: number) => `${n} files selected`,
      SUBMIT: "Evaluate",
      EVALUATING: "Evaluating…",
      // Shown only once a live set exists and at least one slot has a local
      // staged pick — re-hydrates those slots back to what's actually live,
      // one step, no history beyond the current live version.
      DISCARD: "Discard changes",
      // "Restored" implied a server-side rollback happened; nothing on the
      // server changed here — only the local, unpublished picks were dropped.
      DISCARD_TOAST_TITLE: "Changes discarded. Showing the live files again.",
      DISCARD_CONFIRM_TITLE: "Discard these changes?",
      DISCARD_CONFIRM_DESC:
        "This clears every file you've picked or removed on this screen and shows the live files again. Nothing published is affected, but the staged picks themselves are gone.",
      // Distinct from the trigger button's own "Discard changes" label —
      // both would otherwise render with the same accessible name at once,
      // the trigger and the dialog's own confirm action.
      DISCARD_CONFIRM_ACTION: "Yes, discard",
      DISCARD_CONFIRM_CANCEL: "Cancel",
      // CC-B2 — visible only outside production; the backend enforces the
      // same env gate independent of this, so hiding it there is UX only.
      // Pending-state label reuses EVALUATE.SUBMITTING ("Publishing…") below
      // rather than duplicating it.
      PUBLISH_WITHOUT_EVAL: "Publish without evaluation",
      // A slot can be skipped if the company already has a live file for it —
      // this fires only when a slot has neither a pick nor a live file.
      MISSING_WARNING: (names: string) =>
        `Missing: ${names}. This company has no live file for ${names.includes(",") ? "these" : "this"} yet — evaluate is blocked until uploaded.`,
      // Distinct from MISSING_WARNING above: the company DOES have a live
      // file for this slot — the admin removed it in this edit (the trash
      // pill), so "no live file yet" would be wrong and confusing.
      MISSING_WARNING_CLEARED: (names: string) => {
        const plural = names.includes(",");
        // No per-slot undo exists — the only way back to what was live is
        // Discard changes, which wipes every staged edit, not just this one.
        return `${names} ${plural ? "were" : "was"} removed in this edit. Add ${
          plural ? "files" : "a file"
        }, or use Discard changes to undo every edit and start over.`;
      },
      EMPTY_FILE_TITLE: "Empty file",
      // Mirrors the backend's own rejection wording (app/api/context_console.py's
      // _empty_file_error) so the message reads the same whether it's caught here
      // or the file somehow reaches evaluate anyway.
      EMPTY_FILE: (names: string) =>
        `${names} ${names.includes(",") ? "are" : "is"} empty. Choose a file with real content.`,
      REMOVE_LAST_YAML: "At least one *.yml file is required — replace it instead of removing it.",
      REMOVE_LIVE_FAILED: "Couldn't remove that file. Check your connection and try again.",
      DOWNLOAD_LIVE_FAILED: "Couldn't download that file. Check your connection and try again.",
      CARRY_FORWARD_YAMLS_FAILED:
        "Couldn't load the rest of the live YAML files. Check your connection and try again.",
      CONFLICT_TITLE: "This file is already live under that name",
      CONFLICT_DESC: (names: string) =>
        `${names} ${names.includes(",") ? "already exist" : "already exists"} live with different content. Replace ${names.includes(",") ? "them" : "it"}, or cancel this pick and choose a different file.`,
      // Deliberately not just "Replace" — that label is already the slot's
      // own Choose/Replace picker button, and a same-named test/query needs
      // the two to stay unambiguous.
      CONFLICT_REPLACE: "Replace with this file",
      CONFLICT_CANCEL: "Cancel this pick",
      YAML_WARNING_TITLE: "Keep table names consistent",
      YAML_WARNING: "Use the same table names as the All-tables YAML file to avoid duplicate database entries.",
    },
    EVALUATE: {
      TITLE: "Evaluation of added context",
      DESCRIPTION:
        "Review each file's score and the combined coverage of your business context. Open a row to see the findings and suggested fixes.",
      GAUGE_TITLE: "Business-context coverage",
      GAUGE_DESCRIPTION:
        "How completely the set describes the metrics, tables, and rules IQ needs to answer questions for this client.",
      // Idle state: no evaluation yet and nothing in flight — landing here by
      // any route (stepper, back-and-forth, a skip-evaluation publish) must
      // never auto-fire a request; only this button (or Upload's own
      // Evaluate button) does.
      IDLE_BODY: "This context hasn't been evaluated yet. Run the evaluation to see file scores and combined coverage before publishing.",
      RUN_EVALUATION: "Run evaluation",
      EVAL_COST: (total: number) => `Evaluation cost · ${formatCostUsd(total)} USD`,
      GATE: (threshold: number) => `Recommended: combined ≥ ${threshold}% · each file meets its criteria`,
      GATE_CLEARED_TITLE: "Gate cleared, ready to publish",
      GATE_CLEARED_DESC: (combined: number, threshold: number) =>
        `Combined ${combined}% is above the ${threshold}% threshold and every file passed its own criteria.`,
      GATE_BLOCKED_TITLE: "Gate not cleared",
      GATE_BLOCKED_DESC: (failing: string) =>
        `${failing}. Open the flagged row to see what to fix, then upload a corrected file.`,
      GATE_BLOCKED_COMBINED: (combined: number, threshold: number) =>
        `Combined ${combined}% is below the ${threshold}% threshold`,
      FAILING_FILE: (name: string, score: number) => `${name} scored ${score}`,
      PUBLISH_REASON: (threshold: number) =>
        `The combined score is below ${threshold}% and not every file passed its own criteria. You can still publish if you're satisfied with real-world performance — you'll be asked to confirm first.`,
      COL_FILE: "File",
      COL_SCORE: "Score",
      COL_VALUE: "Value",
      COL_STATUS: "Status",
      PASS: "Pass",
      REWORK: "Rework",
      SCORE_LABEL: (name: string, passMark?: number) =>
        passMark === undefined ? `${name} score` : `${name} score, pass mark ${passMark}`,
      REUPLOAD: "Re-upload fixed file",
      REUPLOAD_WHY:
        "Upload a corrected file to score this row again. Nothing else in the set changes.",
      FILES_NOT_ATTACHED:
        "Files aren't attached anymore — choose them again to publish this score.",
      // Caption on a score that describes the live set, not staged picks.
      LIVE_SET_SCORE: "Score of the context that is live now.",
      LIVE_SET_SCORE_NOTE:
        "Score of the context that is live now. Run an evaluation to score your new files.",
      NOTE_READY: "Publishing writes this set to the database and moves you to test questions.",
      NOTE_BLOCKED: "This set is below threshold — publishing will ask you to confirm.",
      NOTE_PUBLISHED: "Set published. Moving to test questions.",
      // Confirm dialog show when Publish is clicked on a below-threshold set.
      OVERRIDE_GATE_TITLE: "Publish below the score threshold?",
      OVERRIDE_GATE_DESC: (combined: number, threshold: number) =>
        `This set scored ${combined}%, below the ${threshold}% gate. IQ may answer this client's questions less reliably until the flagged files are improved. Publish only if you're satisfied with how it performs in practice.`,
      OVERRIDE_GATE_CONFIRM: "Publish anyway",
      OVERRIDE_GATE_CANCEL: "Cancel",
      SUBMIT: "Publish Context",
      SUBMITTING: "Publishing…",
      PUBLISHED: "Published",
      CONTINUE: "Continue to test questions",
      BACK: "Back",
      PUBLISH_FAILED_TITLE: "Publish failed",
      PUBLISH_FAILED_BODY: "Something went wrong publishing this set. Try again.",
      PUBLISH_FORBIDDEN_TITLE: "You don't have permission to publish",
      PUBLISH_FORBIDDEN_BODY: "Ask an admin for access, or check you're in the right company.",
      // Fallback only — apiErrorMessage's specific backend text is shown
      // whenever the response actually carries one (see ContextConsole.tsx).
      FAILED_BODY: "Evaluation failed. Try again.",
      RETRY: "Retry",
      // A file-validation rejection (empty/oversized/wrong-slot/duplicate/
      // malformed) needs a different file, not another attempt at the same
      // request — Retry would just fail again identically.
      FIX_FILES_HINT: "Go back to Upload files to fix or replace it.",
    },
    TEST: {
      TITLE: "Validation",
      DESCRIPTION:
        "Upload at least 30 questions with expected answers. Validation first checks question quality, then compares iQ's answers with your references against this published context.",
      // Screen-reader announcement once an accuracy run settles.
      RUN_SETTLED_COMPLETE: (accuracy: number) => `Accuracy run complete: ${accuracy}% accuracy.`,
      RUN_SETTLED_FAILED: "Accuracy run failed.",
      // The 2-step sub-process this whole stage walks through — Step 1 active
      // until validation is clean (or a run already exists to resume into),
      // Step 2 active from there on. Bold "Step N:" + a lighter label, same
      // pill styling either state, mirrors the mockup's step indicator.
      STEP_1_LABEL: "Step 1:",
      STEP_1_TEXT: "Question quality check",
      STEP_2_LABEL: "Step 2:",
      STEP_2_TEXT: "Accuracy run",
      SET_LABEL: "Question set",
      SET_HINT: "CSV or JSON, one question and expected answer per row",
      SET_ACCEPT: ".csv,.json",
      SET_SUMMARY: (name: string, n: number) =>
        `${name} · ${n} question${n === 1 ? "" : "s"}`,
      // Paired with " · CSV"/" · JSON" in the header where these render.
      SAMPLE_QUESTION_SET: "Sample question set",
      SAMPLE_FILENAME_CSV: "sample_question_set.csv",
      SAMPLE_FILENAME_JSON: "sample_question_set.json",
      // CC-B3: the company's last validated/run set, not a static sample.
      DOWNLOAD_LAST_USED: "Download previously tested questions",
      VALIDATE_NOTE: "The agent flags anything that is not a complete regression question.",
      VALIDATE_NOTE_AFTER_REPLACE:
        "Update the flagged questions in your file, then choose the corrected file and validate again.",
      VALIDATE: "Validate questions",
      VALIDATING: "Validating…",
      AGENT_TITLE: "Question validation",
      SUMMARY_ALL_OK: (n: number) => `${n} valid · ready to run`,
      SUMMARY_OK: (n: number) => `${n} valid`,
      SUMMARY_FLAGGED: (n: number) => `${n} need rework`,
      VALIDATE_COST: (total: number) => `Validation cost: ${formatCostUsd(total)}`,
      RUN_NOTE_READY: (n: number) =>
        `All ${n} questions are valid. Run the set to get an accuracy read.`,
      RUN_NOTE_CLEAN: (n: number) =>
        `Set is clean — ${n} question${n === 1 ? "" : "s"} ready. Run it to get an accuracy read.`,
      RUN_NOTE_REWORK: (n: number) =>
        `${n} question${n === 1 ? "" : "s"} need work. Update them in your file and validate again, or drop them and run the rest.`,
      DROP_FLAGGED: (n: number) => `Remove ${n} flagged & continue`,
      // Per-row alternative to DROP_FLAGGED — overrides one flagged question
      // at a time instead of dropping every flagged row at once.
      REPLACE_SET: "Replace file & validate again",
      RUN: "Run test questions",
      RETRY_RUN: "Retry",
      RUNNING: "Running",
      RUNNING_PROGRESS: (tier: string, completed: number, total: number) =>
        `${tier} — ${completed} of ${total} questions`,
      RUN_FAILED: "The run failed. Check the question set and try again.",
      STAT_RUN: "Questions run",
      STAT_CORRECT: "Correct",
      STAT_INCORRECT: "Incorrect",
      STAT_UNSUCCESSFUL: "Unsuccessful",
      STAT_CONSISTENCY: "Cross-provider consistency",
      RUN_COST: (total: number) => `Cost: ${formatCostUsd(total)}`,
      AGAINST_SET: "Against set",
      TIER_PRIMARY: "Primary",
      TIER_FALLBACK: "Fallback",
      // Shown alongside the current run's results — the completed run right
      // before it, for a quick before/after comparison.
      PREVIOUS_RUN: "Previous run",
      // Shown instead of PREVIOUS_RUN when the run the backend returned
      // (company-wide latest, since it drops context_set_id) belongs to a
      // different, earlier context set than the one currently published —
      // must not read as "this set's accuracy". Which set it actually ran
      // against is named separately, in the SET_ID_LABEL capsule below.
      PREVIOUS_RUN_OTHER_SET: "Last run",
      SET_ID_LABEL: (setId: string) => `Set id: ${setId}`,
      PREVIOUS_RUN_COUNT: (correct: number, total: number) => `${correct} of ${total} correct`,
      LEVEL_ONE_NOTE:
        "This is a level-1 first pass on simple, clearly-worded questions, measured against the published set on the primary model for every agent. Complex, multi-step, and multi-turn scenarios are not covered here. Test those manually in the web app before you tell the client the context is ready.",
      RERUN_NOTE: "Re-run this any time you publish new context.",
      RUN_AGAIN: "Run again",
      RESET: "Start over",
      RESET_CONFIRM_TITLE: "Start over?",
      RESET_CONFIRM_DESC:
        "This clears every staged file, the evaluation score, the question set, and any accuracy run tracked here, and takes you back to Upload. Nothing already published is affected.",
      // Distinct from the trigger button's own "Start over" label — see
      // DISCARD_CONFIRM_ACTION's comment above for why.
      RESET_CONFIRM_ACTION: "Yes, start over",
      RESET_CONFIRM_CANCEL: "Cancel",
      PARSE_ERROR: "Could not read that file. Use a CSV with a `question` column, or a JSON array of objects.",
      TOO_MANY_QUESTIONS: (count: number, max: number) =>
        `That file has ${count} questions — the validator takes up to ${max} at a time. Split it and run the parts separately.`,
      REPLACE_WHILE_RUNNING_TITLE: "Replace the file while a run is in progress?",
      REPLACE_WHILE_RUNNING_DESC:
        "An accuracy run is still going for the current question set — replacing the file stops tracking it here. The run keeps going on the server and still costs what it costs; you just won't see the result.",
      REPLACE_WHILE_RUNNING_CONFIRM: "Replace anyway",
      REPLACE_WHILE_RUNNING_CANCEL: "Cancel",
      SHOW_ALL_QUESTIONS: "Show all questions",
      SHOW_FLAGGED_ONLY: "Show only flagged questions",
      COPY_RESULTS: "Copy",
      COPY_RESULTS_SUCCESS: "Questions and details copied",
      COPY_RESULTS_FAILED: "Could not copy. Select the text and copy manually.",
      // Links a result row to the real chat its question ran in (opens in a
      // new tab, same /chat/:id the normal chat UI uses).
      VIEW_CHAT: "View chat",
      // This stage's own Back button — distinct from EVALUATE.BACK (a plain
      // "Back"), since this one names where it's actually going.
      BACK_TO_EVALUATE: "Back to evaluate & publish",
    },
    COMPLETE: {
      TITLE_TESTED: "Context layer published, validation complete",
      TITLE_PENDING: "Context layer published, validation pending",
      // Company name inserted when known — omitted (double space collapses
      // via the template) for the rare case userDetails hasn't resolved yet.
      DESCRIPTION_TESTED: (company: string) =>
        `Your context is published${company ? ` for ${company}` : ""}. Review the validation results to check answer accuracy.`,
      DESCRIPTION_PENDING: (company: string) =>
        `Your context is published${company ? ` for ${company}` : ""}. Validate it with questions and expected answers.`,
      UPDATED_AT: (date: string) => `Context updated ${date}`,
      NOT_TESTED: "Not tested yet",
      TESTED_BADGE: (accuracy: number) => `${accuracy}% accuracy`,
      COVERAGE_CAPTION_PUBLISHED: "Published evaluation",
      COVERAGE_CAPTION_TESTED: "Tested evaluation",
      FILE_COUNT: (n: number) => `${n} context file${n === 1 ? "" : "s"} published`,
      RECOMMENDED_COVERAGE: (threshold: number) => `Recommended coverage ≥ ${threshold}%`,
      SECTIONS_PASSED: (pass: number, total: number) => `${pass} of ${total} sections passed`,
      VIEW_FILE_SCORES: "View file scores",
      SIMULATED_CAPTION: "Simulated coverage scores · Answer accuracy is tested separately.",
      SCORES_UNAVAILABLE:
        "File-level scores aren't available after a reload — they're only shown right after you publish in this session.",
      DOWNLOAD_ZIP: "Download context files",
      EDIT_CONTEXT: "Edit context",
      GO_TO_VALIDATION: "Go to Validation",
    },
  },
  NOT_FOUND: {
    CRUMB: "Not found",
    TITLE: "This page doesn't exist",
    DESCRIPTION: "The link may be out of date, or the address has a typo. Head back to chat and pick up from there.",
    ACTION: "Go to chat",
  },
  CHATS: {
    PAGE_TITLE: "Chats",
    NAV_LABEL: "Chats",
    PAGE_SUBTITLE: "Every conversation you've had with iQ",
    SEARCH_PLACEHOLDER: "Search chats by title",
    RECENT_LABEL: "Recent chats",
    EMPTY_STATE: "Start a chat to explore your data — your conversations will appear here.",
    NO_RESULTS: "No chats match",
    ERROR_STATE: "Failed to load your chats. Please try again.",
    LOAD_MORE: "Load more",
    NEW_CHAT: "New chat",
  },
  REQUESTS: {
    PAGE_TITLE: "My Requests",
    NAV_LABEL: "My Requests",
    PAGE_SUBTITLE: "Exported files generated from your chat responses",
    SEARCH_PLACEHOLDER: "Search CSV by name",
    EMPTY_STATE_TITLE: "Chat with iQ to pull data.",
    EMPTY_STATE_DESC: "When your request is processed, downloadable datasets will appear right here.",
    TABLE: {
      FILE_NAME: "File Name",
      QUERY: "Query",
      REQUESTED_ON: "Requested On",
      ACTIONS: "Actions",
    },
    PAGINATION: {
      SHOW_ROWS: "Show rows",
    },
    NO_RESULTS: "No results found for",
    ERROR_STATE: "Failed to load your requests. Please try again.",
    RETRY: "Retry",
    TOOLTIPS: {
      DOWNLOAD: "Download",
      REQUEST_AGAIN: "Request again",
      STATUS: {
        pending: "Pending",
        processing: "Processing\u2026",
        done: "Done",
        failed: "Failed",
      },
    },
  },
  DOWNLOAD: {
    DIALOG: {
      TITLE: "Download data",
      DESCRIPTION: "Select required datasets to proceed",
      LAYOUT_LABEL: "Download your data as",
      // Keys mirror the OutputLayout union ('single' | 'multi') so adding or
      // renaming a layout option requires exactly one change on each side.
      LAYOUTS: {
        SINGLE: "Single file with multiple tabs",
        MULTI: "Multiple files in a zip",
      },
      /** e.g. "Download 3 queries" */
      CONFIRM_LABEL: (count: number) =>
        count === 0 ? "Download" : `Download ${count} ${count === 1 ? "dataset" : "datasets"}`,
      CANCEL_LABEL: "Cancel",
      SELECT_ALL: "Select all",
      /** e.g. "3 of 5 selected" */
      SELECTED_COUNT: (selected: number, total: number) => `${selected} of ${total} selected`,
    },
    BUTTON: {
      DESCRIPTION: "Download the data for easier viewing of large results and ad-hoc analysis",
      ACTION_LABEL: "Download data",
      SUCCESS_LABEL: "Downloaded",
      REQUESTING_LABEL: "Requesting\u2026",
    },
  },
  DEEP_MODE: {
    BANNER: {
      /** Delay (ms) before the in-progress banner appears once Deep Mode is triggered. */
      // TODO(86d3ujcv5): revert to Step 2 completion once backend latency improves
      SHOW_DELAY_MS: 5000,
      TRIGGERED: {
        HEADING: "That's a pretty complex question, switching to Deep Mode so that I can really think this through.",
        SUBTEXT: "It may take up to 3–4 minutes to get back with a solid answer",
        SUBTEXT_EMPHASIS: "3–4 minutes",
        FOOTER: "You can keep working — we'll ping you here",
      },
      ERROR: {
        HEADING: "Hit a snag — retrying with a simplified approach.",
        SUBTEXT: "This may take a little longer than usual.",
        SUBTEXT_EMPHASIS: "little longer",
        FOOTER: "Retrying…",
        BADGE: "ISSUE",
      },
      CANCELLED: {
        HEADING: "Deep Mode was cancelled before completing.",
        SUBTEXT: "Analysis stopped before finishing. You can re-send your question to try again.",
        FOOTER: "Cancelled",
      },
    },
    TOOLTIP: {
      TITLE: "Your deep mode answer is ready",
      VIEW_ANSWER: "View answer",
    },
    ICONS: {
      TRIGGERED: '🧠',
      ERROR: '⚠️',
      CANCELLED: '🚫',
    },
  },
  CHAT: {
    // Export actions above the top right of a markdown table in a chat Summary:
    // Copy table + Download CSV buttons, and a `⋮` overflow menu (Copy as Markdown).
    TABLE_EXPORT: {
      MENU: "More table actions",
      DOWNLOAD_CSV: "Download CSV",
      COPY_TABLE: "Copy table",
      COPY_MARKDOWN: "Copy as Markdown",
      // Success toast after a copy, keyed by format.
      COPIED: {
        table: "Table copied",
        markdown: "Table copied as Markdown",
      },
      // Error toast when the clipboard write is denied (e.g. the page lost focus).
      COPY_FAILED: "Couldn't copy the table. Please try again.",
      // Suffixed with the local date: `iq_table_2026-09-29.csv`.
      CSV_FILENAME_PREFIX: "iq_table",
    },
    // `⋮` export menu in a chart / scorecard card header in a chat's Visualisation tab.
    // "Copy chart" puts a PNG on the clipboard — the only chart form a web page
    // can hand to Docs / Word / Slides — and "Copy data" the underlying table.
    CHART_EXPORT: {
      MENU_CHART: "Chart actions",
      MENU_SCORECARD: "Scorecard actions",
      // Group labels inside the menu.
      GROUP_IMAGE: "Image",
      GROUP_DATA: "Data",
      COPY_CHART: "Copy chart",
      COPY_SCORECARD: "Copy scorecard",
      COPY_DATA: "Copy data",
      DOWNLOAD_PNG: "Download PNG",
      DOWNLOAD_CSV: "Download CSV",
      COPIED: {
        image: "Chart copied as an image",
        table: "Chart data copied",
      },
      // Error toasts when the clipboard write is denied or the image fails to render.
      COPY_FAILED: "Couldn't copy the chart. Please try again.",
      DOWNLOAD_FAILED: "Couldn't download the chart image. Please try again.",
      // Used when the chart has no title: `iq_chart_2026-09-29.png`.
      FILENAME_PREFIX: "iq_chart",
    },
    CANCELLED: {
      HEADING: "Response was cancelled.",
      SUBTEXT: "You can re-send your question to try again.",
      FOOTER: "Cancelled",
      RETRY: "Retry",
    },
    /** Composer Stop button — used for both the tooltip and the aria-label so a
     * screen reader announces one consistent name for the control. */
    STOP_TOOLTIP: "Stop iQ Response",
    /** Default Composer placeholder (plain and rich-text modes). */
    COMPOSER_PLACEHOLDER: "Ask “what”, “how”, or “when” about your sales, customers, or marketing performance…",
    PROCESSING_TIMER: {
      /** How long each status line stays visible while a turn is in flight. */
      ROTATE_INTERVAL_MS: 5000,
      /**
       * Header status before the first execution step arrives: advances one line per
       * interval and holds on the last (never wraps). The last line also shows once
       * every step has completed.
       */
      STATUS_LINES: [
        "Pulling your data together\u2026",
        "Running the numbers\u2026",
        "Scanning for patterns\u2026",
        "Connecting the dots\u2026",
        "Building your answer\u2026",
      ],
      TOOK: (duration: string) => `Took ${duration}`,
      ELAPSED_ARIA: (status: string, elapsed: string) => `${status} Elapsed ${elapsed}`,
    },
  },
  CHAT_SHARE: {
    LINK_COPIED: "Share link copied to clipboard.",
    CREATED: "Share link created.",
    REVOKED: "Share link revoked.",
    FORK_SUCCESS: "Chat added to your conversations.",
    ERROR: "Could not create share link. Please try again.",
    COPY_ERROR: "Failed to copy link. Please copy it manually.",
    REVOKE_ERROR: "Could not revoke share link. Please try again.",
    NO_ACTIVE_LINKS: "No active share links to revoke.",
    FORK_ERROR: "Could not fork chat. Please try again.",
    NOT_FOUND: "This link is invalid or has been revoked.",
    LOAD_ERROR: "Could not load this shared chat. Please try again.",
    EXPIRED: "This share link has expired.",
    SOURCE_DELETED: "The original chat has been deleted.",
    INVALID: "This link is invalid.",
    CROSS_COMPANY: "This link belongs to a different company. You must be in the same company to view it.",
    ACCESS_DENIED: "You don't have access to this shared chat.",
    READ_ONLY_HINT: "This is a read-only view. Continue the chat to ask follow-up questions.",
  },
  // Conversational dashboard creation (DashboardSessionView).
  DASHBOARD_SESSION: {
    /** Under the preview header after the server reported an edit turn failed. */
    LAST_TURN_NOT_APPLIED: "Your last change was not applied. Publishing will save the draft shown.",
    /** Same note when the server named the rejected change (the error event's `request`). */
    LAST_TURN_NOT_APPLIED_NAMED: (request: string) =>
      `Your change "${request}" was not applied. Publishing will save the draft shown.`,
    /** Label over a rejected change's reply (the draft is unchanged). */
    NOTICE_NOT_APPLIED: "Change not applied",
    /** Label over a rejected publish's reply (the draft is unchanged and not saved). */
    NOTICE_NOT_PUBLISHED: "Not published",
    /** Label over any other failed turn's reply from the server. */
    NOTICE_FAILED: "Something went wrong",
    /** A failed turn's reply text when the server sent none. */
    TURN_FAILED_FALLBACK: "Something went wrong with this turn.",
    /** Heading over a turn's unmet asks (`not_applied`) under the agent message. */
    NOT_APPLIED_TITLE: "Not applied",
    /** Lead-in before an unmet ask's nearest buildable option. */
    NOT_APPLIED_ALTERNATIVE: "Nearest option:",
  },
  // Build progress rail inside the dashboard builder's agent bubble.
  DASHBOARD_BUILD: {
    /** Row label per backend step id. `restore` is drawn only with a prior draft;
     * `save` folds into the done line and is drawn only when it failed. */
    STEPS: {
      restore: "Restoring draft",
      catalog: "Catalog loaded",
      schemas: "Reading schemas",
      draft: "Drafting blocks",
      checks: "Checking spec",
      save: "Saving draft",
    },
    /** The `save` row's label on a publish turn (it wraps the publish). */
    PUBLISH_STEP: "Publishing dashboard",
    RAIL_LABEL: "Build steps",
    RUNNING: "running",
    FAILED: "Failed",
    PASSED: "Passed",
    NOT_NEEDED: "Not needed",
    /** Clarify turn: the `draft` row's detail (status paused, never "done"). */
    WAITING: "Waiting for your answer",
    /** Edit turn with a prior draft, while `draft` runs. */
    UPDATING: "Updating draft…",
    ELAPSED: "elapsed",
    PAUSED: "paused",
    DETAILS: "Details",
    LOG_LABEL: "Build events",
    TABLES: (n: number) => `${n} ${n === 1 ? "table" : "tables"}`,
    UNREADABLE_COUNT: (n: number) => `${n} unreadable`,
    PRELOADED: (n: number) => `${n} preloaded`,
    TABLES_READ: (n: number) => `${n} ${n === 1 ? "table" : "tables"} read`,
    BLOCKS: (n: number) => `${n} ${n === 1 ? "block" : "blocks"}`,
    ATTEMPTS: (n: number) => `${n} attempts`,
    ISSUES: (n: number) => `${n} ${n === 1 ? "issue" : "issues"}`,
    /** Collapsed done lines. `time` is m:ss. The count says "blocks", like the
     * `draft` row it repeats. */
    DONE_DRAFT: (time: string, blocks: number) =>
      `Built in ${time} · ${blocks} ${blocks === 1 ? "block" : "blocks"}`,
    DONE_PUBLISHED: (version: number | null, time: string) =>
      version != null ? `Published v${version} · ${time}` : `Published · ${time}`,
    DONE_CLARIFY: (time: string) => `Waiting for your answer · ${time}`,
    DONE_NO_CHANGE: (time: string) => `No change needed · ${time}`,
    DONE_REJECTED: (time: string) => `Change rejected at Checking spec · ${time}`,
    NOT_APPLIED: (n: number) => `${n} not applied`,
    FAILED_AT: (label: string) => `Build failed at ${label}`,
    UNREADABLE: {
      /** Chip text after the table name. */
      YAML_ERROR: "YAML error",
      /** A schema file that parsed but is not a valid schema. */
      SCHEMA_ERROR: "invalid schema file",
      LINE: "line",
      SHOW_LINE: "Show line",
      HIDE_LINE: "Hide line",
    },
  },
  // Admin-only "Conversation" sheet on a published dashboard (Debug Mode > Inspect).
  DASHBOARD_CONVERSATION: {
    TITLE: "Conversation",
    MENU_LABEL: "Conversation",
    LOADING: "Loading conversation",
    EMPTY: "No conversation is available for this dashboard",
    UNAVAILABLE: "Conversation is not available",
    LOAD_ERROR: "Couldn't load the conversation. Please try again.",
    RETRY: "Retry",
    COPY: "Copy prompt",
    COPIED: "Copied",
    COPY_HINT: "Copy the original prompt (the first message)",
    READ_ONLY_PLACEHOLDER: "Editing published dashboards is coming soon",
  },
  // Admin widget instruction edit in the inspector's Instruction tab.
  DASHBOARD_WIDGET_EDIT: {
    EDIT: "Edit",
    CANCEL: "Cancel",
    TEST: "Test",
    TESTING: "Testing…",
    PUBLISH: "Publish",
    PUBLISHING: "Publishing…",
    CONFIRM_PUBLISH: "Confirm publish",
    BACK: "Back",
    RETRY: "Retry",
    TEXT_LABEL: "Widget instruction",
    NOTE_LABEL: "Change note (optional)",
    NOTE_PLACEHOLDER: "What changed and why",
    LOADING: "Loading the widget's current text",
    LOAD_ERROR: "Couldn't load the widget's current text.",
    NOT_IN_VERSION:
      "This widget's query is not in the current version. Reload the dashboard and edit again.",
    NO_WIDGET: "This query is not tied to a widget, so it can't be edited here.",
    INEXACT_WARNING:
      "Could not separate the creator's text from the column contract. Edit the full text carefully.",
    INSTRUCTION_EYEBROW: "Instruction · editable",
    SYSTEM_TEXT_EYEBROW: "Added by the system",
    SYSTEM_TEXT_HINT: "Column and format rules iQ builds from the widget definition. Rebuilt each time the instruction is tested and published. Not editable.",
    SYSTEM_TEXT_HINT_EDITING: "iQ rebuilds these rules from the widget definition when you test and publish. Not editable.",
    TEST_PROGRESS:
      "Generating SQL and running it on the owner company. Large widgets can take a few minutes.",
    TEST_ERROR: "Couldn't test this change.",
    // The client gave up waiting; minutes come from DRAFT_TEST_TIMEOUT_MS so they can't drift.
    TEST_TIMEOUT_ERROR: (minutes: number) =>
      `The test did not finish in ${minutes} ${minutes === 1 ? "minute" : "minutes"}. Try again; narrower date ranges or fewer rows finish faster.`,
    PUBLISH_HINT: "Publish turns on after a test passes.",
    TEST_NOT_RECORDED: "The test passed but could not be saved. Test again to publish.",
    RESULT_TITLE: "Test result",
    ROW_COUNT: (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "row" : "rows"}`,
    ROWS_SHOWN: (shown: number, total: number) =>
      `Showing ${shown.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} rows`,
    COLUMNS: "Columns",
    CHECK_PASSED: "Column check passed",
    CHECK_FAILED: "Column check failed",
    MISSING_COLUMNS: "Missing columns",
    EXTRA_COLUMNS: "Extra columns",
    NO_ROWS: "The query returned no rows.",
    SHOW_SQL: "Show SQL",
    HIDE_SQL: "Hide SQL",
    QUERY_FAILED: "Query failed",
    CONFIRM_TITLE: "Publish this change?",
    DIFF_LABEL: "Changes to the instruction",
    REACH: (n: number) => `Used by ${n.toLocaleString("en-US")} ${n === 1 ? "company" : "companies"}`,
    REACH_LOADING: "Checking which companies use this dashboard…",
    REACH_UNKNOWN: "Couldn't check how many companies use this dashboard.",
    PUBLISHED: (n: number) =>
      `Published. SQL for ${n.toLocaleString("en-US")} ${n === 1 ? "company is" : "companies are"} being prepared.`,
    CONFLICT: "Someone published a newer version. Reload and edit again.",
    RELOAD: "Reload",
    PUBLISH_ERROR: "Couldn't publish this change.",
    // History sheet toggle. Drafts are hidden until it is on; the count shows while hidden.
    SHOW_DRAFTS: "Show drafts",
    SHOW_DRAFTS_COUNT: (n: number) => `Show drafts (${n.toLocaleString("en-US")})`,
  },
  // Admin "History" sheet on a published dashboard (Debug Mode > Inspect).
  DASHBOARD_HISTORY: {
    TITLE: "History",
    MENU_LABEL: "History",
    LOADING: "Loading versions",
    EMPTY: "No versions to show yet.",
    UNAVAILABLE: "Version history is not available.",
    LOAD_ERROR: "Couldn't load the version history.",
    RETRY: "Retry",
    VERSION: (n: number) => `Version ${n}`,
    CURRENT: "Current",
    DRAFT: "Draft",
    AUTHOR: (id: number) => `User ${id}`,
    AUTHOR_SELF: "You",
    AUTHOR_UNKNOWN: "Unknown author",
    CHANGED_WIDGETS: "Changed widgets",
    RESTORE: "Restore",
    RESTORING: "Restoring…",
    CONFIRM_RESTORE: "Confirm restore",
    CANCEL: "Cancel",
    RESTORE_CONFIRM_TITLE: (n: number) => `Restore version ${n}?`,
    RESTORE_CONFIRM_BODY:
      "This makes a new current version with this version's widgets. Nothing is deleted.",
    RESTORED: (n: number) =>
      `Restored. SQL for ${n.toLocaleString("en-US")} ${n === 1 ? "company is" : "companies are"} being prepared.`,
    RESTORE_ERROR: "Couldn't restore this version.",
    // Discard on a draft row (shown with Show drafts on).
    DISCARD: "Discard",
    DISCARDING: "Discarding…",
    CONFIRM_DISCARD: "Confirm discard",
    DISCARD_CONFIRM_TITLE: (n: number) => `Discard draft ${n}?`,
    DISCARD_CONFIRM_BODY: "It leaves History. Nothing that is published changes.",
    DISCARDED: (n: number) => `Draft ${n} discarded.`,
    DISCARD_ERROR: "Couldn't discard this draft.",
    DISCARD_PUBLISHED: "This version is already published, so it can't be discarded.",
    DISCARD_MISSING: "This draft no longer exists.",
  },
  DASHBOARD_ADMIN: {
    // Covers BOTH stages of the per-widget hard refresh (the SQL clear and the
    // forced re-executes): whichever one failed, the admin's next move is the
    // same. The Sentry tag `stage` is what says which, for us.
    WIDGET_HARD_REFRESH_ERROR: "Couldn't hard refresh this widget. Please try again.",
    /**
     * The clear landed and the SQL was regenerated, but the re-run itself failed
     * (a query-level `error` on the result). A retry would run the same failing
     * SQL, so this does NOT say "try again" — the widget shows the backend error.
     */
    WIDGET_HARD_REFRESH_QUERY_FAILED: "SQL was regenerated, but the query failed. See the widget for the error.",
    /** The two admin entries on a widget's right-click menu. */
    MENU_HARD_REFRESH: "Hard refresh",
    MENU_CLEAR_COMPANY_SQL: "Clear SQL for a company…",
    /** The query picker the widget entry adds to the company-clear dialog. */
    CLEAR_SQL: {
      QUERIES_LABEL: "Queries",
      ADD_QUERIES: "Add widgets",
      ADD_ALL: "Add all on this dashboard",
      REMOVE_ALL: "Remove all",
      SEARCH_PLACEHOLDER: "Search by widget name or query ID",
      NO_MATCH: "No widgets match",
      EMPTY_PICKER: "No queries picked. Add at least one widget.",
      /** A pasted id that matches no widget in this dashboard's spec. */
      OFF_DASHBOARD_NAME: "Not on this dashboard",
      /** Shown once any picked id is off-dashboard — the company list widens. */
      WIDENED_COMPANY_LIST: "Showing all companies because a query is from another dashboard.",
      /** Shape refusal: 32-char hex, or a legacy integer id. */
      BAD_ID: "That isn't a query ID. Use a 32-character hex ID or a number.",
      COPY_ID_HINT: "Click to copy the full ID",
      ID_COPIED: "Query ID copied",
      /** Primary button label in widget mode; `count` is the picked-row count. */
      CLEAR_N_QUERIES: (count: number) => `Clear SQL for ${count} quer${count === 1 ? "y" : "ies"}`,
      /** Footer action under the picker search when a typed id is not on this dashboard. */
      ADD_ID: (id: string) => `Add ${id}`,
      /** aria-label of a picked row's remove button. */
      REMOVE_ROW: (name: string) => `Remove ${name}`,
    },
  },
  DASHBOARD_MATRIX: {
    /** Under a matrix whose pinned zone passed the 50% cap; `count` is what still sticks. */
    PIN_CAP_NOTE: (count: number) => `Showing ${count} pinned. The rest scroll.`,
    /** Pin glyph tooltips and the matching context-menu items. */
    PIN_ROW: "Pin row",
    UNPIN_ROW: "Unpin row",
    PIN_COLUMN: "Pin column",
    UNPIN_COLUMN: "Unpin column",
    /** aria-label of a pin glyph (a toggle: `aria-pressed` carries the state). */
    PIN_LABEL: (name: string) => `Pin ${name}`,
    UNPIN_ALL: "Unpin all",
    /** Clears the reader's own view of this widget; shown only when one exists. */
    RESET_VIEW: "Reset to dashboard default",
    /** Band-corner period sort control: visible text, and its accessible name. */
    SORT_LABEL: "Period",
    SORT_ARIA_LABEL: "Period, newest first",
    /** Sort control tooltip: what a click does next. */
    SORT_NEWEST_FIRST: "Newest first",
    SORT_OLDEST_FIRST: "Oldest first",
    /** Band-corner micro-label while the reader's view differs from the dashboard's. */
    YOUR_VIEW: "Your view",
    YOUR_VIEW_HINT: "Pinned or sorted by you. Reset from the widget menu.",
  },
};

export const ROLE = {
  GODVIEW: "SarasAdmin",
  IQ_USER: "IQUser",
  IQ_ADMIN: "IQAdmin",
  IQ_DASHBOARD_ADMIN: "IQDashboardAdmin",
  DASHBOARD_CREATOR: "DashboardCreator",
}

/**
 * The admin roles that get a blanket capability grant in `hasAppPermision`,
 * mirroring iq-backend's `ADMIN_ROLES`. Lives here rather than in
 * `constants/rbac.tsx` because that module cannot import `ROLE` — it would close
 * the cycle rbac.tsx → constants/index.tsx → hooks/useRbac → rbac.tsx.
 */
export const ADMIN_ROLES = [ROLE.GODVIEW, ROLE.IQ_DASHBOARD_ADMIN];

// ALERTS moved to `./uiConstants` (it embeds the InviteUser/Icon components).

export const GODVIEW = {
  SEARCH_TYPE: [
    { value: "company", label: "Company Name" },
    { value: "email", label: "User Email" },
    { value: "chat", label: "Chat ID" },
  ],
  PLATFORM_LABELS: {
    web: "Web",
    slack: "Slack",
    teams: "Teams",
    mcp: "MCP",
  } as Record<string, string>,
}

/** Subscription-service identifiers the admin Subscription Management page keys on (not UI copy). */
export const SUBSCRIPTION_MANAGEMENT = {
  /** `productName` the admin subscription endpoints take for IQ. */
  PRODUCT_NAME: "IQ",
  /** Credit packs for "Add credits" are the product's RESOURCE add-ons; `ruleValue` is the credits. */
  CREDIT_PACK_ADDON_TYPE: "RESOURCE",
  /** Plan name the subscription service gives every IQ trial, live or ended. */
  TRIAL_PLAN_NAME: "TRIAL_IQ_TRIAL",
};


export const LINKS = {
  TALK_TO_US: 'https://go.sarasanalytics.com/meetings/schedule-consultation/lets-talk-data?utm_source=iq&utm_medium=app&utm_campaign=book-a-demo',
  SETTING_UP_PULSE: 'https://help.sarasanalytics.com/getting-started/setting-up-your-pulse-account',
  // Opens one Slack workspace (by team id, e.g. "T123ABC") in the Slack web
  // client, which hands off to the desktop app when it is installed.
  SLACK_WORKSPACE: (teamId: string) => `https://app.slack.com/client/${teamId}`,
  // Slack's own redirect into the chat with one bot user: opens the desktop app
  // when installed, else the browser. Preferred over SLACK_WORKSPACE when the
  // bot id is known.
  SLACK_APP_REDIRECT: (teamId: string, botUserId: string) =>
    `https://slack.com/app_redirect?team=${teamId}&channel=${botUserId}`,
  // The Saras IQ app's page in Teams (Store listing once published), by app id.
  TEAMS_APP: (appId: string) => `https://teams.microsoft.com/l/app/${appId}`,
  // A 1:1 chat with the IQ bot in Teams, by the bot's Azure app id.
  TEAMS_BOT_CHAT: (botId: string) => `https://teams.microsoft.com/l/chat/0/0?users=28:${botId}`,
}

export const CHART_TYPES = {
  BAR: "bar",
  BAR_CHART: "bar_chart",
};
export const TAB_IDS = {
  SQL: "code",
  CHART: "chart",
  TABLE: "table",
} as const;

export const MESSAGE_SOURCES = {
  CHARTING_AGENT: "charting_agent",
  SQL: "SQL",
  SQL_QUERY_GENERATED: "Sql_Query_Generated",
  CUSTOMER_REP: "Customer_Service_Representative",
  RECOMMENDED_QUESTIONS: "recommended_questions",
  LOADING_MESSAGE_ID: "LoadingMsg",
  AGENT_MESSAGE: "Agent_Message",
  AGENT_HEADER: "Agent_Header",
};

export const STREAM_EVENT_TYPES = {
  USER_MESSAGE: "user_message",
  BOT_MESSAGE: "bot_message",
  MEMORY_CONTEXT: "memory_context",
  MEMORY_SUGGESTION: "memory_suggestion",
  APPLIED_CONTEXT: "applied_context",
  COMPLEXITY_CLASSIFIER: "Complexity_Classifier",
} as const;

export const STREAM_CONTENT_SOURCES = {
  COMPLEXITY_CLASSIFIER: 'Complexity_Classifier',
} as const;

export const STREAM_CONTENT_TYPES = {
  COMPLEXITY_INDICATOR: 'Complexity_Indicator',
} as const;

export const STREAM_MODES = {
  DEEP_MODE: 'deep mode',
  ERROR: 'error',
  CANCELLED: 'cancelled',
} as const;

export const SSE_DATA_PREFIX = 'data:';

/**
 * Inner dashboard event types. These ride the shared chat message stream
 * (`POST /chats/:id/messages/stream`) nested inside a `bot_message` envelope's
 * `data.content`; the dashboard SSE reader unwraps the envelope and dispatches
 * on these. Distinct from the chat stream's own envelope types.
 */
export const DASHBOARD_SESSION_EVENT_TYPES = {
  PROGRESS: "dashboard_progress",
  READY: "dashboard_ready",
  PREVIEW_DATA: "dashboard_preview_data",
  DONE: "dashboard_agent_done",
  ERROR: "error",
  /** First-turn clarifying question or decline from the readiness agent (no draft yet). */
  TEXT_MESSAGE: "TextMessage",
  /** One code-sourced build step (transient; never in a reloaded transcript). */
  BUILD_STEP: "dashboard_build_step",
  /** The turn's build summary, saved at most once per turn, before the terminal event. */
  BUILD_SUMMARY: "dashboard_build_summary",
} as const;

export const SUGGESTIONS = {
  questions: [
    `What's our customer retention rate this quarter?`,
    `What is our Net Promoter Score trend?`,
    `What are our top-performing sales reps?`,
    "What strategies can we implement to boost sales?",
    `How can we improve customer retention rates?`,
    `What strategies can we implement to boost sales?`,
  ],
};

// SETTINGS moved to `./uiConstants` (it embeds the Icon component).

export const ERRORS = {
  MEMORY_SUGGESTION: {
    SAVE_FAILED: "Couldn't save. Nothing was stored, so try again.",
    DISMISS_FAILED: "Couldn't dismiss this suggestion. Try again.",
  },
  AUTH: {
    PARSE_USER: "Failed to parse user object from query params",
  },
  WAREHOUSE_ERROR: {
    title: "",
    description: "",
  },
  WAREHOUSE_CREATING_ERROR: {
    title: "",
    description: "",
  },
  LOAD_CHAT_ERROR: {
    title: "",
    description: "We couldn't load your chats right now",
  },
  LOAD_MORE_CHAT_ERROR: {
    title: "",
    description: "Looks like we hit a snag loading older chats",
  },
  LOAD_MESSAGES_ERROR: {
    title: "This conversation isn't loading now",
    description:
      "We are facing some technical issues. Please retry or try after some time. If the issue persists, please contact support.",
  },
  LOAD_MORE_MESSAGES_ERROR: {
    title: "Couldn't fetch earlier messages",
    description:
      "We are facing some technical issues. Please retry or try after some time. If the issue persists, please contact support.",
  },
  SENT_MESSAGES_ERROR: {
    title: "You'll need to connect relevant data sources to get insights",
    description: "Connect relevant sources to start asking questions",
  },
  CHAT_RESPONSE_ERROR: {
    title: "Error",
    description: "Your subscription is not valid or you have used all your available tokens. Please contact our support team for further assistance."
  },
  CHAT_RESPONSE_ERROR_DEFAULT: {
    title: "Sorry! I couldn't get response for your query",
    description: "Please retry. If this keeps happening report the issue.",
  },
  // Conversational dashboard-creation stream. Kept distinct from a genuine
  // network drop (NetworkDisconnectError) so an HTTP 5xx server refusal reads as
  // "the server is busy, retry" rather than "you lost connection".
  DASHBOARD_SESSION: {
    SERVICE_UNAVAILABLE: "The service is temporarily unavailable. Try again in a moment.",
  },
  // Shared across every `fetch`-based SSE reader (chat + dashboard streams): the
  // client-side stream ceiling (`STREAM_TIMEOUT_MS`) was hit before the server
  // closed the stream. Distinct from a network drop — the request was still open,
  // it just ran too long — so the copy points at retrying rather than reconnecting.
  STREAM: {
    TIMEOUT: "This is taking longer than expected. Please try again.",
  },
};

export const KEYBOARD_KEYS = {
  ENTER: "Enter",
  SHIFT_ENTER: "Shift-Enter",
} as const;

/**
 * Words after a code fence ("```sql") that are read as the block's language; any
 * other text after the fence is the block's content. Covers the languages
 * `ui/code-block` highlights plus their common short names.
 */
export const CODE_FENCE_LANGUAGES: ReadonlySet<string> = new Set([
  "sql",
  "json",
  "javascript",
  "js",
  "typescript",
  "ts",
  "python",
  "py",
  "bash",
  "sh",
  "shell",
]);

export const POSTHOG = {
  EVENTS: {
    USER_MANAGEMENT_PAGE: 'user_management_page',
    COPY_BOT_MESSAGE: 'copy_bot_message',
    THREAD_CREATED: 'thread_created',
    // A "New chat" primary action fired from a chat surface; `source`
    // distinguishes the origin (e.g. the Chats library page).
    NEW_CHAT_CLICKED: 'new_chat_clicked',
    THUMBS_UP: 'thumbs_up',
    THREAD_DELETED: 'thread_deleted',
    USER_INVITED: 'user_invited',
    USER_DELETED: 'user_deleted',
    USER_REINVITED: 'user_reinvited',
    MANAGE_GROUP_PAGE: 'manage_group_page',
    DATA_GROUP_CREATED: 'data_group_created',
    DATA_GROUP_UPDATED: 'data_group_updated',
    DATA_GROUP_DELETED: 'data_group_deleted',
    REPORT_ISSUE_SUBMITTED: 'report_issue_submitted',
    SLACK_INTEGRATION_SUCCESS: 'slack_integration_success',
    SLACK_INTEGRATION_DISCONNECT: 'slack_integration_disconnect',
    // The PERSONAL Slack link (one user's identity), not the workspace install.
    // CLICKED fires when the confirm dialog opens, UNLINKED only after the
    // DELETE succeeds — watch the gap for people backing out of the dialog.
    SLACK_USER_UNLINK_CLICKED: 'slack_user_unlink_clicked',
    SLACK_USER_UNLINKED: 'slack_user_unlinked',
    // The not-linked row's "Open Slack" shortcut. Fires on click only; the link
    // itself happens later in Slack, so this measures intent, not linking.
    SLACK_USER_OPEN_SLACK_CLICKED: 'slack_user_open_slack_clicked',
    // The second Slack app ("IQ Data Engineer", backend app_key `pulse`). Kept
    // as distinct event names so the existing `slack_*` insights keep meaning
    // IQ installs only.
    PULSE_SLACK_BUTTON_CLICKED: 'pulse_slack_button_clicked',
    PULSE_SLACK_INTEGRATION_SUCCESS: 'pulse_slack_integration_success',
    PULSE_SLACK_INTEGRATION_DISCONNECT: 'pulse_slack_integration_disconnect',
    THEME_CHANGED: 'theme_changed',
    SUGGESTED_QUESTION_CLICKED: 'suggested_question_clicked',
    SLACK_BUTTON_CLICKED: 'slack_button_clicked',
    // Microsoft Teams integration events (admin setup and user linking)
    TEAMS_BUTTON_CLICKED: 'teams_button_clicked',
    TEAMS_INTEGRATION_SUCCESS: 'teams_integration_success',
    TEAMS_INTEGRATION_DISCONNECT: 'teams_integration_disconnect',
    TEAMS_ACCOUNT_UNLINK: 'teams_account_unlink',
    TEAMS_STORE_LINK_CLICKED: 'teams_store_link_clicked',
    TEAMS_OPEN_IN_TEAMS_CLICKED: 'teams_open_in_teams_clicked',
    MCP_URL_COPIED: 'mcp_url_copied',
    // MCP setup guides: VIEWED, URL_COPIED and GUIDE_STEP_OPENED all carry
    // `{ client: "claude" | "codex" | "grok" | "gemini" }`; STEP_OPENED adds `{ tab, step }`.
    MCP_SETUP_VIEWED: 'mcp_setup_viewed',
    MCP_GUIDE_STEP_OPENED: 'mcp_guide_step_opened',
    // In-app "Update Available" prompt (nav card + explicit popup). SHOWN fires
    // once per pending update; APPLIED carries `{ was_streaming }`; DEFERRED
    // carries `{ source: "popup" | "processing" }` — "popup" when the call-out is
    // dismissed, "processing" when the mid-response guard is declined ("Keep
    // waiting"). Watch SHOWN vs APPLIED for accept rate.
    APP_UPDATE_PROMPT_SHOWN: 'app_update_prompt_shown',
    APP_UPDATE_APPLIED: 'app_update_applied',
    APP_UPDATE_DEFERRED: 'app_update_deferred',
    // Context Console admin flow (upload -> evaluate -> publish -> validate ->
    // run). Evaluate-completion isn't tracked here — that mutation lives in
    // FileEvaluationContext.tsx (shared, mounted above the router) with no
    // success callback exposed to the page; adding one to distinguish a real
    // evaluate from redisplaying a leftover result needs a context change,
    // not just a page-level trackEvent call.
    CONTEXT_CONSOLE_PUBLISHED: 'context_console_published',
    CONTEXT_CONSOLE_QUESTIONS_VALIDATED: 'context_console_questions_validated',
    CONTEXT_CONSOLE_RUN_STARTED: 'context_console_run_started',
    SOFT_ERROR_DISPLAYED: 'soft_error_displayed',
    DASHBOARD_NAV_ITEM_CLICKED: 'dashboard_nav_item_clicked',
    // Collapse/expand of a left-nav section (Published / Drafts / a use-case group).
    DASHBOARD_NAV_SECTION_TOGGLED: 'dashboard_nav_section_toggled',
    // The use-case dashboard view auto-collapsed the left nav to the icon rail on a
    // laptop-width viewport (`{ use_case_id, viewport_width }`), and the user pushing
    // back by re-expanding it (`{ use_case_id }`) — the override is the signal that
    // tells us the auto-collapse is unwelcome, so both must be read together.
    DASHBOARD_NAV_AUTO_COLLAPSED: 'dashboard_nav_auto_collapsed',
    DASHBOARD_NAV_AUTO_COLLAPSE_OVERRIDDEN: 'dashboard_nav_auto_collapse_overridden',
    // Published/Drafts landing controls (Manage-surface overhaul). `view` = grid|list;
    // `sort` = the active sort key; `dashboard_search_used` fires once per focused
    // search session (debounced), not per keystroke.
    DASHBOARD_VIEW_TOGGLED: 'dashboard_view_toggled',
    DASHBOARD_SORT_CHANGED: 'dashboard_sort_changed',
    DASHBOARD_SEARCH_USED: 'dashboard_search_used',
    DASHBOARD_VIEWED: 'dashboard_viewed',
    DASHBOARD_CREATE_STARTED: 'dashboard_create_started',
    DASHBOARD_CREATED: 'dashboard_created',
    DASHBOARD_CREATE_FAILED: 'dashboard_create_failed',
    // Conversational (session-based) dashboard creation.
    DASHBOARD_SESSION_STARTED: 'dashboard_session_started',
    DASHBOARD_SESSION_MESSAGE_SENT: 'dashboard_session_message_sent',
    DASHBOARD_DRAFT_RECEIVED: 'dashboard_draft_received',
    DASHBOARD_SESSION_CONFIRMED: 'dashboard_session_confirmed',
    DASHBOARD_SESSION_ABANDONED: 'dashboard_session_abandoned',
    DASHBOARD_SESSION_FAILED: 'dashboard_session_failed',
    // The server reported an edit turn failed over an existing draft, so the
    // "not applied" note showed. Once per failure. PostHog-only.
    DASHBOARD_SESSION_TURN_NOT_APPLIED: 'dashboard_session_turn_not_applied',
    // An agent reply listed asks it did not do (`not_applied`), with `{ count }`.
    // Once per turn, live only (a reload does not count it again). PostHog-only.
    DASHBOARD_SESSION_NOT_APPLIED_SHOWN: 'dashboard_session_not_applied_shown',
    // The agent replied with a clarifying question or decline (a `TextMessage`).
    // Once per turn, live only. PostHog-only.
    DASHBOARD_SESSION_CLARIFICATION_SHOWN: 'dashboard_session_clarification_shown',
    // Usage & adoption (PRD M4): captured at user/company/dashboard/tab/widget
    // level. The `company` PostHog group (set in AuthContext) gives the
    // company-level rollup; dashboard/tab/widget dims ride each event via
    // DashboardTrackingContext.
    DASHBOARD_TAB_VIEWED: 'dashboard_tab_viewed',
    DASHBOARD_WIDGET_VIEWED: 'dashboard_widget_viewed',
    DASHBOARD_WIDGET_CLICKED: 'dashboard_widget_clicked',
    DASHBOARD_WIDGET_INTERACTION: 'dashboard_widget_interaction',
    DASHBOARD_FILTER_CHANGED: 'dashboard_filter_changed',
    DASHBOARD_DRILLDOWN: 'dashboard_drilldown',
    DASHBOARD_REFRESHED: 'dashboard_refreshed',
    // Admin-only "Hard refresh": clears the query result cache, then re-runs the
    // batch with `force_refresh`. PostHog-only (the backend allowlist in
    // `dashboardMetrics.ts` doesn't know this event yet).
    DASHBOARD_HARD_REFRESHED: 'dashboard_hard_refreshed',
    DASHBOARD_REFRESH_FAILED: 'dashboard_refresh_failed',
    // Admin-only "Clear SQL for a company": drops a TARGET company's cached
    // generated SQL for this dashboard. PostHog-only (not in the backend
    // allowlist in `dashboardMetrics.ts`) — it's an operator action, not adoption.
    DASHBOARD_ADMIN_SQL_CLEARED: 'dashboard_admin_sql_cleared',
    // SarasAdmin "View as user" (impersonated dashboard read). PostHog-only —
    // deliberately NOT in `BACKEND_ALLOWED_EVENTS`, so an admin's impersonated
    // browsing never lands in the customer-facing adoption metrics.
    DASHBOARD_VIEW_AS_STARTED: 'dashboard_view_as_started',
    DASHBOARD_VIEW_AS_EXITED: 'dashboard_view_as_exited',
    DASHBOARD_SESSION_ENDED: 'dashboard_session_ended',
    DASHBOARD_WIDGET_NO_DATA: 'dashboard_widget_no_data',
    // Interactive chart legend (openui >= 0.14): a click hides/shows a series or
    // slice, a double-click isolates it. PostHog-only — deliberately NOT in
    // `BACKEND_ALLOWED_EVENTS` (the backend action Literal doesn't know it, and one
    // unknown action 400s the whole adoption batch).
    DASHBOARD_LEGEND_TOGGLED: 'dashboard_legend_toggled',
    DASHBOARD_MATRIX_GRAIN_CHANGED: 'dashboard_matrix_grain_changed',
    DASHBOARD_MATRIX_ROW_TOGGLED: 'dashboard_matrix_row_toggled',
    // Reader view of a matrix (pins + period sort, saved per reader). Payload is
    // `widget_type` + `widget_id` plus: pin → `axis` ('row'|'column'), `pinned`,
    // `entry` ('glyph'|'menu'|'keyboard'); sort → `direction`; reset → `entry`
    // ('menu' = widget menu, 'context_menu' = pin menu). `entry`, not `source`:
    // `source` is the tracking provider's view/preview dimension.
    DASHBOARD_WIDGET_PIN_TOGGLED: 'dashboard_widget_pin_toggled',
    DASHBOARD_WIDGET_SORT_TOGGLED: 'dashboard_widget_sort_toggled',
    DASHBOARD_WIDGET_VIEW_RESET: 'dashboard_widget_view_reset',
    // "Unpin all" from the pin context menu. Payload: `widget_type` + `widget_id`
    // and `entry` ('context_menu'), like the reset event.
    DASHBOARD_WIDGET_PINS_CLEARED: 'dashboard_widget_pins_cleared',
    DASHBOARD_CHOROPLETH_METRIC_CHANGED: 'dashboard_choropleth_metric_changed',
    DASHBOARD_SECTION_TOGGLED: 'dashboard_section_toggled',
    // Per-widget collapse chevron (`Visualization.collapsible`); carries
    // `collapsed: true|false` alongside the usual widget dimensions.
    DASHBOARD_WIDGET_COLLAPSED: 'dashboard_widget_collapsed',
    // Design-language preset switch (iq / grain / cm), orthogonal to light/dark.
    DASHBOARD_THEME_CHANGED: 'dashboard_theme_changed',
    DASHBOARD_WIDGET_EXPORTED: 'dashboard_widget_exported',
    DASHBOARD_WIDGET_COPIED: 'dashboard_widget_copied',
    DASHBOARD_FILTERS_RESET: 'dashboard_filters_reset',
    DASHBOARD_LOAD_FAILED: 'dashboard_load_failed',
    // Widget-level performance telemetry. One summary per batch completion
    // (initial load / tab switch / filter delta / refresh / expand); the slow
    // event is per-widget detail, fired only above SLOW_WIDGET_MS.
    DASHBOARD_LOAD_SUMMARY: 'dashboard_load_summary',
    DASHBOARD_WIDGET_SLOW: 'dashboard_widget_slow',
    DASHBOARD_ACCESS_DENIED: 'dashboard_access_denied',
    // The dashboard loaded, but its DATA access was revoked mid-life: the batch
    // execute 403s and the view collapses to the access-removed panel. Distinct from
    // DASHBOARD_ACCESS_DENIED, which is the dashboard itself being unreachable.
    DASHBOARD_ACCESS_REMOVED: 'dashboard_access_removed',
    DASHBOARD_DRAFT_DELETED: 'dashboard_draft_deleted',
    DASHBOARD_WIDGET_ERROR: 'dashboard_widget_error',
    // Fired when an arrangement resolver drops an unrecognized/invalid value the
    // agent authored (bad layout block, out-of-range kpiColumns/colSpan, …) so
    // "agent asked for X, UI ignored it" is diagnosable instead of silent.
    DASHBOARD_ARRANGEMENT_DROPPED: 'dashboard_arrangement_dropped',
    // End-user "Dashboards home" (use-cases grid): a use-case card opened, drilling
    // into that use case's first dashboard.
    DASHBOARD_USE_CASE_OPENED: 'dashboard_use_case_opened',
    // Creator "Assign dashboards" accordion (promoted from the page-local
    // ASSIGN_EVENTS const now that this module is shared-owned again).
    DASHBOARD_ASSIGNMENTS_USE_CASE_EXPANDED: 'dashboard_assignments_use_case_expanded',
    DASHBOARD_ASSIGNMENT_ADDED: 'dashboard_assignment_added',
    DASHBOARD_ASSIGNMENT_REMOVED: 'dashboard_assignment_removed',
    DASHBOARD_ASSIGNMENT_REORDERED: 'dashboard_assignment_reordered',
    // A use case was created from the merged "Use cases" tab's Create dialog.
    DASHBOARD_USE_CASE_CREATED: 'dashboard_use_case_created',
    // Dashboard sharing (persona-branched Share dialog). `opened` when the dialog
    // opens; `submitted` on a share run (carries scope + dashboard/recipient counts
    // + success/fail tallies); `revoked` when an existing share is removed from the
    // "Who has access" disclosure.
    DASHBOARD_SHARE_OPENED: 'dashboard_share_opened',
    DASHBOARD_SHARE_SUBMITTED: 'dashboard_share_submitted',
    DASHBOARD_SHARE_REVOKED: 'dashboard_share_revoked',
    // The "Who has access" roster in the share panel rendered successfully (carries
    // the grant `count`) — measures how often the audit-first panel surfaces the
    // current access list, distinct from opening the panel.
    DASHBOARD_SHARE_ACCESS_VIEWED: 'dashboard_share_access_viewed',
    // Debug/inspect chrome (creator-only, low volume). `inspected` carries
    // `entry: "overlay" | "icon" | "menu"` so we can tell which affordance opened
    // the panel; `debug_toggled` carries `enabled`.
    DASHBOARD_WIDGET_INSPECTED: 'dashboard_widget_inspected',
    DASHBOARD_DEBUG_TOGGLED: 'dashboard_debug_toggled',
    // Admin-only "Conversation" sheet (iq:admin:dashboard:write holders). Payload is
    // IDs + `view_type` + `has_prompt` + `message_count` only — NEVER any message
    // text. PostHog-only: deliberately not in `BACKEND_ALLOWED_EVENTS`
    // (services/dashboardMetrics.ts). `copied` = the original (first) prompt.
    DASHBOARD_PROMPT_OPENED: 'dashboard_prompt_opened',
    DASHBOARD_PROMPT_COPIED: 'dashboard_prompt_copied',
    // Admin widget instruction edit (iq:admin:dashboard:write). IDs, counts and
    // booleans only — NEVER the instruction text. PostHog-only, like the prompt
    // events above: the backend emits its own widget_edit_* metrics events.
    // `tested` carries `is_passed`; `published` carries `companies_scheduled_count`.
    DASHBOARD_WIDGET_EDIT_OPENED: 'dashboard_widget_edit_opened',
    DASHBOARD_WIDGET_EDIT_TESTED: 'dashboard_widget_edit_tested',
    DASHBOARD_WIDGET_EDIT_PUBLISHED: 'dashboard_widget_edit_published',
    // Build progress rail in the dashboard builder. Ids and counts only, never
    // text. `shown` fires once per live turn; `unreadable_table_shown` carries
    // `count`; `rail_expanded` carries `state: done | clarify`.
    DASHBOARD_BUILD_PROGRESS_SHOWN: 'dashboard_build_progress_shown',
    DASHBOARD_BUILD_UNREADABLE_TABLE_SHOWN: 'dashboard_build_unreadable_table_shown',
    DASHBOARD_BUILD_RAIL_EXPANDED: 'dashboard_build_rail_expanded',
    DASHBOARD_BUILD_DETAILS_OPENED: 'dashboard_build_details_opened',
    DASHBOARD_VERSION_RESTORED: 'dashboard_version_restored',
    // A draft discarded from History. Carries the draft's `version_id`.
    DASHBOARD_WIDGET_DRAFT_DISCARDED: 'dashboard_widget_draft_discarded',
    // The agent-authored "ⓘ" explainer on a KPI/widget was opened — measures how
    // much the generated metric definitions are actually read.
    DASHBOARD_WIDGET_INFO_OPENED: 'dashboard_widget_info_opened',
    // Retry from a widget/KPI error card. Pairs with DASHBOARD_WIDGET_ERROR so the
    // error → retry → recovery funnel is measurable.
    DASHBOARD_WIDGET_RETRIED: 'dashboard_widget_retried',
    // "Report a data issue" funnel on a single widget: opened → reported /
    // cancelled / failed. `reported` carries `query_id` and `description_length`
    // (never the text itself — the description can quote customer data).
    DASHBOARD_WIDGET_ISSUE_REPORT_OPENED: 'dashboard_widget_issue_report_opened',
    DASHBOARD_WIDGET_ISSUE_REPORTED: 'dashboard_widget_issue_reported',
    DASHBOARD_WIDGET_ISSUE_REPORT_CANCELLED: 'dashboard_widget_issue_report_cancelled',
    DASHBOARD_WIDGET_ISSUE_REPORT_FAILED: 'dashboard_widget_issue_report_failed',
    // App-wide (not dashboard-scoped): the persistent breadcrumb strip. Fired via
    // `trackEvent` directly — the breadcrumb renders outside DashboardTrackingProvider.
    BREADCRUMB_CLICKED: 'breadcrumb_clicked',
    // Chat sharing events
    CHAT_SHARE_CREATED: 'chat_share_created',
    CHAT_SHARE_LINK_COPIED: 'chat_share_link_copied',
    CHAT_SHARE_FORKED: 'chat_share_forked',
    CHAT_SHARE_REVOKED: 'chat_share_revoked',
    CHAT_SHARE_VIEWED: 'chat_share_viewed',
    // "Download CSV" / "Copy markdown" in a markdown table's export menu in a chat Summary;
    // carry the table's `row_count`/`column_count` (shape only, never cell values).
    CHAT_TABLE_CSV_DOWNLOADED: 'chat_table_csv_downloaded',
    // `format`: 'table' (HTML + TSV, pastes as a table) | 'markdown' (GFM source).
    CHAT_TABLE_COPIED: 'chat_table_copied',
    // Same actions in a chart / scorecard's export menu in the Visualisation tab; also carry
    // the normalized `chart_type` (e.g. 'bar', 'scorecard').
    CHAT_CHART_CSV_DOWNLOADED: 'chat_chart_csv_downloaded',
    // `format`: 'image' (PNG of the chart) | 'table' (its data as HTML + TSV).
    CHAT_CHART_COPIED: 'chat_chart_copied',
    CHAT_CHART_PNG_DOWNLOADED: 'chat_chart_png_downloaded',
    // Share entry points + share dialog button presses. `source` is
    // `POSTHOG.PROPERTY_VALUES.CHAT_ACTION_SOURCE` (chat screen header vs NavRail
    // chat options); the `*_CREATED` / `*_REVOKED` events above are the success
    // outcomes, fired from the mutation hooks in `useChatShare`.
    CHAT_SHARE_CLICKED: 'chat_share_clicked',
    CHAT_SHARE_CREATE_CLICKED: 'chat_share_create_clicked',
    CHAT_SHARE_REVOKE_CLICKED: 'chat_share_revoke_clicked',
    // NavRail chat options "Delete" press; THREAD_DELETED is the success outcome.
    CHAT_DELETE_CLICKED: 'chat_delete_clicked',
    // User menu → Product Switch nested items; `product` is `daton` | `pulse`.
    PRODUCT_SWITCHER_CLICKED: 'product_switcher_clicked',
    // Reports. Search events carry `query_length` / `result_count`, never the text.
    REPORT_TAB_CHANGED: 'report_tab_changed',
    REPORT_TEMPLATES_SEARCHED: 'report_templates_searched',
    REPORT_TEMPLATES_CATEGORY_CHANGED: 'report_templates_category_changed',
    REPORT_TEMPLATE_PREVIEW_OPENED: 'report_template_preview_opened',
    REPORT_TEMPLATE_ACTION_SELECTED: 'report_template_action_selected',
    REPORT_PREVIEW_DOWNLOADED: 'report_preview_downloaded',
    REPORT_PREVIEW_THUMBNAILS_TOGGLED: 'report_preview_thumbnails_toggled',
    REPORT_PREVIEW_PAGE_CLICKED: 'report_preview_page_clicked',
    // `*_SUBMITTED` is the button press; `*_SENT` / `*_SAVED` / `*_FAILED` are the
    // API outcome, fired from the mutation hooks in `useReports`. `*_CANCEL_CLICKED`
    // is the Cancel button only — closing the dialog with X / Escape / outside
    // click sends nothing, so measure drop-off as action_selected → submitted.
    REPORT_SEND_NOW_SUBMITTED: 'report_send_now_submitted',
    REPORT_SEND_NOW_SENT: 'report_send_now_sent',
    REPORT_SEND_NOW_FAILED: 'report_send_now_failed',
    REPORT_SEND_NOW_CANCEL_CLICKED: 'report_send_now_cancel_clicked',
    REPORT_SCHEDULE_SUBMITTED: 'report_schedule_submitted',
    REPORT_SCHEDULE_SAVED: 'report_schedule_saved',
    REPORT_SCHEDULE_SAVE_FAILED: 'report_schedule_save_failed',
    REPORT_SCHEDULE_CANCEL_CLICKED: 'report_schedule_cancel_clicked',
    REPORT_TYPE_FILTER_CHANGED: 'report_type_filter_changed',
    REPORT_STATUS_FILTER_CHANGED: 'report_status_filter_changed',
    REPORT_LIST_SEARCHED: 'report_list_searched',
    REPORT_EDIT_CLICKED: 'report_edit_clicked',
    REPORT_PAUSE_TOGGLED: 'report_pause_toggled',
    REPORT_HISTORY_VIEWED: 'report_history_viewed',
    REPORT_RETRY_CLICKED: 'report_retry_clicked',
    // My Requests. Search carries `query_length` / `result_count`, never the text.
    MY_REQUESTS_QUERY_CLICKED: 'my_requests_query_clicked',
    MY_REQUESTS_SEARCHED: 'my_requests_searched',
    MY_REQUESTS_DOWNLOAD_CLICKED: 'my_requests_download_clicked',
    MY_REQUESTS_REQUEST_AGAIN_CLICKED: 'my_requests_request_again_clicked',
    // Privacy-safe message provenance event. Never attach memory text, slug, or tags.
    APPLIED_CONTEXT_OPENED: 'applied_context_opened',
    // Consent card for extracted memories. Never attach descriptions; only counts/results.
    MEMORY_SUGGESTION_SHOWN: 'memory_suggestion_shown',
    MEMORY_SUGGESTION_SAVED: 'memory_suggestion_saved',
    MEMORY_SUGGESTION_DISMISSED: 'memory_suggestion_dismissed',
    MEMORY_SUGGESTION_SAVE_FAILED: 'memory_suggestion_save_failed',
    MEMORY_SETTINGS_VIEWED: 'memory_settings_viewed',
    MEMORY_SEARCH_USED: 'memory_search_used',
    MEMORY_DETAIL_OPENED: 'memory_detail_opened',
    MEMORY_DELETED: 'memory_deleted',
    MEMORY_DELETE_FAILED: 'memory_delete_failed',
    TAB_STATUS_RETURN: 'tab_status_return',
    // Never attach memory content — only the resulting enabled state.
    MEMORY_SETTINGS_TOGGLED: 'memory_settings_toggled',
    MEMORY_SETTINGS_TOGGLE_FAILED: 'memory_settings_toggle_failed',
    ADMIN_MEMORY_TOGGLED: 'admin_memory_toggled',
    ADMIN_MEMORY_TOGGLE_FAILED: 'admin_memory_toggle_failed',
    // Settings > Instructions. Never attach instruction text, rejection issues,
    // or generated facts — only counts, error codes, and enabled state.
    USER_INSTRUCTIONS_VIEWED: 'user_instructions_viewed',
    USER_INSTRUCTIONS_SAVED: 'user_instructions_saved',
    USER_INSTRUCTIONS_SAVE_FAILED: 'user_instructions_save_failed',
    USER_INSTRUCTIONS_TOGGLED: 'user_instructions_toggled',
    USER_INSTRUCTIONS_TOGGLE_FAILED: 'user_instructions_toggle_failed',
    USER_INSTRUCTIONS_SUGGESTION_ADDED: 'user_instructions_suggestion_added',
    USER_INSTRUCTIONS_DISCARDED: 'user_instructions_discarded',
    USER_INSTRUCTIONS_REPLACE_CANCELLED: 'user_instructions_replace_cancelled',
  },
  // PostHog group-analytics group types (company-level rollup).
  GROUPS: {
    COMPANY: 'company',
  },
  // Bounded values for event properties, so every call site sends the same spelling.
  PROPERTY_VALUES: {
    // Where a chat action (share, delete) was started from.
    CHAT_ACTION_SOURCE: {
      CHAT_SCREEN: 'chat_screen',
      NAV_RAIL: 'nav_rail',
    } as const,
    PRODUCT: {
      DATON: 'daton',
      PULSE: 'pulse',
    } as const,
    // Where a report template's "Use Template" action was picked from.
    REPORT_TEMPLATE_ACTION_SOURCE: {
      CARD: 'card',
      PREVIEW: 'preview',
    } as const,
    // Whether the schedule form is creating a new schedule or editing one.
    SCHEDULE_MODE: {
      CREATE: 'create',
      EDIT: 'edit',
    } as const,
    // Which control opened a scheduled report's send history.
    REPORT_HISTORY_SOURCE: {
      MENU: 'menu',
      ERROR_BADGE: 'error_badge',
    } as const,
  },
  // Search events fire once the user pauses typing, not per keystroke.
  SEARCH_TRACKING_DEBOUNCE_MS: 800,
  // PostHog feature-flag keys. Keys are editable in the PostHog UI — if renamed,
  // update both there and here.
  FEATURE_FLAGS: {
    DASHBOARDS: 'iq-dashboards',
    // Gates the "grain" design-language preset per environment. TRUE → grain,
    // FALSE → iq. Off for everyone but selected emails until grain passes a
    // gradual release; rollback is killing the flag (no deploy). See
    // POSTHOG_INTEGRATION.md and DashboardThemeProvider.
    GRAIN_THEME: 'grain-theme-enabled',
    // NOTE: `iq-dashboard-context` (DASHBOARD_CONTEXT) was RETIRED with schema
    // v3 — `spec.context` and `visibleWhen: { contextKey }` no longer exist at
    // render time. `normalizeSpecToV3` bakes each context condition into a static
    // `uiState.defaults` boolean, exactly as the backend migrator does, so there
    // is nothing left to gate. The PostHog flag itself can be archived.
    //
    // Gates the schema-v3 ACTION EVALUATOR only (filter `change`, tab `select`,
    // root `load` → ui-state paths; see `DashboardUiStateContext`). OFF → the
    // spec's `uiState.defaults` still apply, so a dashboard is correct and
    // crash-free, just without dynamic reveal/dim; ON → actions fire. Ships OFF
    // in prod until the webapp v3 engine has soaked in dev/qa; flipping it on is
    // a PostHog change, not a deploy. Keep in sync with the PostHog UI.
    DASHBOARD_V3_ACTIONS: 'iq-dashboard-v3-actions',
    // Gates use-case creation (the "Create use case" button + dialog on the
    // Use cases tab). OFF hides the create affordance; the tab, heading, and
    // list stay visible. Keep in sync with the PostHog UI.
    USECASE_CREATE: 'iq-usecase-create',
    // Gates the whole Reports section — the rail destination (expanded and
    // collapsed) and the `/reports` route, which redirects to chat when off.
    // Reports is not ready for prod, so this ships OFF there and ON in
    // dev/qa; turning it on is a PostHog change, not a deploy.
    REPORTS: 'iq-reports',
    // Gates the whole Context Console section — the Settings nav row, the
    // /settings/contextConsole route (redirects to the user's default section
    // when off), the Settings badge and the live-set fetch. Ships OFF in prod
    // until the backend /context/* APIs land there, ON in dev/qa; flipping it is a
    // PostHog change, not a deploy. Keep in sync with the PostHog UI.
    CONTEXT_CONSOLE: 'iq-context-console',
    // Gates the "IQ Data Engineer" Slack card on Settings > Integrations (the
    // second Slack app, backend `app_key` = "pulse"). OFF hides the whole row —
    // the Slack and Claude (MCP) rows are unaffected. Ships ON in dev/qa and OFF
    // in prod until the pulse app launches; turning it on is a PostHog change,
    // not a deploy. Keep in sync with the PostHog UI.
    PULSE_DE_AGENT: 'iq-pulse-de-agent',
    // Gates the SarasAdmin "View as user" control on the dashboard + use-case
    // dashboard views. The role gate is separate and stricter (ROLE.GODVIEW only —
    // the backend 403s IQDashboardAdmin for doAs), so the flag alone never exposes
    // it. Keep in sync with the PostHog UI.
    VIEW_AS_USER: 'iq-view-as-user',
    // Gates the "Your Reports" tab on the Reports page (scheduled/sent reports
    // table). OFF → only the Templates tab renders (TabsTemplate drops the tab
    // strip for a single tab). Ships OFF in prod until scheduled reports are
    // ready; turning it on is a PostHog change, not a deploy.
    SCHEDULE_REPORT: 'iq-schedule-report',
    // Gates the whole chat-sharing feature — the "Share chat" header button, the
    // "Share" chat context-menu item, and the `/chat/share/:token` recipient view,
    // which redirects to chat when off. Ships OFF in prod until sharing is ready;
    // turning it on is a PostHog change, not a deploy.
    SHARE_CHAT: 'iq-share-chat',
    // Gates the in-app "Ready to update" nav-footer card (`UpdatePromptStrip`).
    // Card-only: the `vite.config.ts` prompt-mode switch is build-time and cannot
    // be flagged, so a rollback of the card still leaves the SW in prompt mode.
    // Ships OFF in prod at merge (standing dark-ship pattern); turning it on is a
    // PostHog change, not a deploy. Keep in sync with the PostHog UI.
    UPDATE_PROMPT: 'iq-update-prompt',
    MEMORY_UI: 'iq-memory-ui',
    // Gates Settings > Instructions — the nav row, the route, and the
    // `/api/user-instructions` read. A backend 404 (feature off there) also hides
    // the section, so turning this on ahead of the backend is safe. Ships OFF in
    // prod; turning it on is a PostHog change, not a deploy. Keep in sync with
    // the PostHog UI.
    USER_INSTRUCTIONS: 'iq-user-instructions',
    // Gates the rich text composer in the chat input. OFF → plain textarea,
    // ON → rich text editor with markdown support. Rollout per environment is
    // managed in PostHog, not in code.
    RICH_TEXT_COMPOSER: 'iq-composer-richtext',
    // Gates the Microsoft Teams integration UI (admin setup card on Settings >
    // Integrations and user linking on Settings > Account Integrations). Ships OFF
    // in prod until Teams integration is ready; turning it on is a PostHog change,
    // not a deploy. Keep in sync with the PostHog UI.
    TEAMS_INTEGRATION: 'iq-teams-integration',
    TAB_STATUS: 'iq-tab-status',
    // Gates the chat `⋮` export menu (`DataExportMenu`) on Summary tables,
    // Visualisation-tab charts and scorecards. Its image copy/download rests on
    // `html-to-image`, which is slow to get upstream fixes, so prod can kill the
    // whole menu without a deploy. OFF → tables render as plain openui tables and
    // charts without the menu. Ships ON in dev/qa, OFF in prod. Keep in sync
    // with the PostHog UI.
    DATA_EXPORT_MENU: 'iq-data-export-menu',
    // Gates the admin widget instruction edit: the inspector's Edit / Test /
    // Publish and the Inspect menu's History sheet. The role gate is separate
    // (exact iq:admin:dashboard:write), so the flag alone never exposes it. Ships
    // ON in dev/qa and OFF in prod until the backend routes are there; turning it
    // on is a PostHog change, not a deploy. Keep in sync with the PostHog UI.
    DASHBOARD_WIDGET_EDIT: 'iq-dashboard-widget-edit',
    // Gates the agency variant of the nav, always paired with the
    // AGENCY_USER_MANAGEMENT_DASHBOARD subscription add-on (see
    // useAgencyFeatureAccess — the add-on is checked first and is
    // authoritative, this flag is the rollout switch). Read from AppRail.tsx
    // (hides Reports), Settings.tsx and UserManagement*.tsx (Manage Group /
    // Manage Users visibility and the per-row Edit-group action). Keep in
    // sync with the PostHog UI.
    IQ_FOR_AGENCIES: 'iq-for-agencies',
    // Brand-account counterpart of IQ_FOR_AGENCIES, paired with the
    // BRAND_USER_MANAGEMENT_DASHBOARD add-on (see useBrandFeatureAccess).
    // Same call sites as IQ_FOR_AGENCIES — Manage Group is agency-OR-brand,
    // not agency-only. Keep in sync with the PostHog UI.
    IQ_FOR_BRANDS: 'iq-for-brands',
    // Gates matrix/table freeze (pinned rows and columns from the spec's `freeze`)
    // and the matrix period sort (`timeAxis.sort`). OFF or not yet loaded → both
    // spec fields are ignored and widgets render exactly as before. Ships ON in
    // dev/qa and OFF in prod until verified there; turning it on is a PostHog
    // change, not a deploy. Keep in sync with the PostHog UI.
    DASHBOARD_FREEZE: 'iq-dashboard-freeze',
    // Gates the build progress rail in the dashboard builder (step rows, unreadable
    // table chip, done line, first-build preview skeleton). OFF or not yet loaded →
    // the builder renders exactly as before. The backend gates its step and summary
    // events with the same key. Ships ON in dev/qa and OFF in prod until verified
    // there; turning it on is a PostHog change, not a deploy. Keep in sync with the
    // PostHog UI.
    DASHBOARD_BUILD_PROGRESS: 'iq-dashboard-build-progress',
  },
  MESSAGES: {
    POSTHOG_INIT_ERROR: "PostHog initialization failed:",
    POSTHOG_CONFIG_MISSING: "PostHog configuration missing. Analytics will not be initialized.",
  }
};
